import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import {
  getCreatedAt,
  getGeneralMessageCreatedBy,
  getGeneralMessageSender,
  getGeneralMessageSentAt,
  getGeneralMessageStatus
} from 'Src/helpers/accessor/accessor';
import { Attachment, GeneralMessage, GeneralMessageCopyOptions } from 'Src/models/generalMessage';
import { getSelectOptionsForGeneralMessagesStatus } from 'Src/helpers/table/select';
import {
  deleteSchoolGeneralMessage,
  getAllSchoolGeneralMessages,
  getSchoolGeneralMessage,
  getSchoolGeneralMessageDetails,
  getSchoolGeneralMessageForEditor,
  getSchoolGeneralMessageStatusCounts,
  getSchoolGeneralMessageSummaries,
  getSchoolGeneralMessages,
  getSchoolGeneralMessagesCount,
  returnSchoolGeneralMessageToDraft,
  scheduleSchoolGeneralMessage,
  sendSchoolGeneralMessage,
  updateSchoolGeneralMessage
} from 'Src/helpers/service/admin/generalMessage';

import './GeneralMessages.css';
import { GENERAL_MESSAGE_STATUS, GENERAL_MESSAGE_TABS, GENERAL_MESSAGE_STATUS_TYPE } from 'Src/consts/generalMessage';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { Button } from 'Src/components/Button/Button';
import { ScheduleInfo } from 'Src/components/GeneralMessages/ScheduleInfo/ScheduleInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import { GeneralMessageContent } from 'Src/components/GeneralMessageSummary/MessageContent';
import { GeneralMessageTextForm } from 'Src/components/GeneralMessageTextForm/GeneralMessageTextForm';
import { GeneralMessagePreview } from 'Src/components/GeneralMessagePreview/GeneralMessagePreview';
import { GeneralMessageSubscribers } from 'Src/components/GeneralMessageSubscribers/GeneralMessageSubscribers';
import { MessageDeliveryStatus } from 'Src/components/GeneralMessageSummary/MessageDeliveryStatus';
import { uploadFileAllPath } from 'Src/helpers/service/file';
import {
  DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
  getGeneralMessageAttachmentContentHash,
  getGeneralMessageAttachmentUploadError,
  getGeneralMessageDuplicateAttachmentError,
  getGeneralMessageRequestError,
  isGeneralMessageAttachmentDuplicate,
  validateGeneralMessageAttachmentAddition,
  validateGeneralMessageAttachmentCollection
} from 'Src/helpers/generalMessageAttachments/generalMessageAttachmentSize';
import propz from 'propz';
import { HTML_FIRST_PART_PAGE_CODE, HTML_LAST_PART_PAGE_CODE } from 'Src/components/HTMLEditorQuill/HTMLEditorConst';
import { getSettingsUploadFiles } from 'Src/helpers/service/nobody/settings';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import moment from 'moment';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import {
  PageHelpAnchor,
  PageHelpDefinition,
  PageHelpFilter,
  PageHelpObservation,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';

const GENERAL_MESSAGES_COLUMNS_VIEW_KEY = 'communications.generalMessages';
const GENERAL_MESSAGES_MOBILE_COLUMNS_VIEW_KEY = 'communications.generalMessages.mobile';
const GENERAL_MESSAGES_MOBILE_MEDIA_QUERY = '(max-width: 900px)';
const GENERAL_MESSAGES_MOBILE_HIDDEN_COLUMNS = ['channels', 'audience', 'created'];
const GENERAL_MESSAGES_REQUIRED_COLUMN = 'message';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Title',
    field: 'title',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['title']
  },
  {
    text: 'Status',
    field: 'statusOptions',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getGeneralMessageStatus
  },
  {
    text: 'Created by',
    field: 'createdBy',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getGeneralMessageCreatedBy
  },
  {
    text: 'Created at',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAt
  },
  {
    text: 'Sent by',
    field: 'sender',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getGeneralMessageSender
  },
  {
    text: 'Sent at',
    field: 'sentAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getGeneralMessageSentAt
  }
];

interface State {
  items: GeneralMessage[];
  currentPage: number;
  selectedItems: GeneralMessage[];
  generalMessage: GeneralMessage;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  draftFilters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;

  currentTabIndex: number;
  isEditable: boolean;
  isError: boolean;
  errorMessage: string;
  isLoadingFile: boolean;

  isMessageRefreshing: boolean;

  messageSmsText: string;
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;

  isSmsEditorEnabled: boolean;
  isPushEditorEnabled: boolean;
  isEmailEditorEnabled: boolean;

  messageAttachments: Attachment[];
  maxSizeUploadFile: number;
  isImageError: boolean;
  activeStatus: GENERAL_MESSAGE_STATUS_TYPE;
  sentScope: 'current' | 'archive';
  sentTotal: number;
  sentCurrentTotal: number;
  sentArchiveTotal: number;
  scheduledTotal: number;
  draftTotal: number;
  searchText: string;
  appliedSearchText: string;
  channelFilter: string;
  draftChannelFilter: string;
  deliveryFilter: string;
  draftDeliveryFilter: string;
  drawerChannel: 'sms' | 'email' | 'push';
  deliveryStatsChannel: 'overview' | 'sms' | 'email' | 'push';
  isDeleteModalOpen: boolean;
  isCopyModalOpen: boolean;
  isCopyPreparing: boolean;
  copySourceMessage?: GeneralMessage;
  copyOptions: GeneralMessageCopyOptions;
  toastMessage: string;
  audienceCountById: { [messageId: string]: number };
  hiddenColumns: string[];
  isMobileView: boolean;
  isColumnsMenuOpen: boolean;
  scheduledAction: 'send' | 'reschedule' | 'draft' | null;
  scheduledActionMessage?: GeneralMessage;
  scheduledActionAt: Date | null;
  scheduledActionError: string;
  isScheduledActionInProgress: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class GeneralMessages extends React.Component<Props, State> {
  private messageDetailRequests: { [messageId: string]: Promise<GeneralMessage> } = {};
  private listRequestVersion = 0;
  private uploadSizeRequest?: Promise<number>;
  private mobileViewMediaQuery?: MediaQueryList;
  private pendingAttachmentContentHashes = new Set<string>();
  private pageHelpRegistration?: PageHelpRegistration;
  private disabledChannelDrafts: {
    email?: { subject: string; body: string; attachments: Attachment[] };
    push?: { title: string; body: string };
    sms?: { text: string };
  } = {};

  constructor(props) {
    super(props);

    const isMobileView =
      typeof window !== 'undefined' && window.matchMedia(GENERAL_MESSAGES_MOBILE_MEDIA_QUERY).matches;
    const requestedActiveStatus = propz.get(props, ['location', 'state', 'activeStatus']);
    const initialActiveStatus = [
      GENERAL_MESSAGE_STATUS.SENT,
      GENERAL_MESSAGE_STATUS.SCHEDULED,
      GENERAL_MESSAGE_STATUS.DRAFT
    ].includes(requestedActiveStatus)
      ? requestedActiveStatus
      : GENERAL_MESSAGE_STATUS.SENT;

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      generalMessage: undefined,
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      draftFilters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      isError: false,
      errorMessage: '',

      currentTabIndex: 0,
      isEditable: false,
      isLoadingFile: false,

      isMessageRefreshing: false,

      messageSmsText: '',
      messageEmailSubject: '',
      messageEmailBody: '',
      messagePushTitle: '',
      messagePushBody: '',

      isSmsEditorEnabled: true,
      isPushEditorEnabled: true,
      isEmailEditorEnabled: true,

      messageAttachments: [],
      maxSizeUploadFile: DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
      isImageError: false,
      activeStatus: initialActiveStatus as GENERAL_MESSAGE_STATUS_TYPE,
      sentScope: 'current',
      sentTotal: 0,
      sentCurrentTotal: 0,
      sentArchiveTotal: 0,
      scheduledTotal: 0,
      draftTotal: 0,
      searchText: '',
      appliedSearchText: '',
      channelFilter: '',
      draftChannelFilter: '',
      deliveryFilter: '',
      draftDeliveryFilter: '',
      drawerChannel: 'email',
      deliveryStatsChannel: 'overview',
      isDeleteModalOpen: false,
      isCopyModalOpen: false,
      isCopyPreparing: false,
      copySourceMessage: undefined,
      copyOptions: {
        title: true,
        content: true,
        recipients: false
      },
      toastMessage: '',
      audienceCountById: {},
      hiddenColumns: this.loadHiddenColumnsForView(isMobileView),
      isMobileView,
      isColumnsMenuOpen: false,
      scheduledAction: null,
      scheduledActionMessage: undefined,
      scheduledActionAt: null,
      scheduledActionError: '',
      isScheduledActionInProgress: false
    };
  }

  private getColumnsViewKey = (isMobileView: boolean = this.state.isMobileView): string =>
    isMobileView ? GENERAL_MESSAGES_MOBILE_COLUMNS_VIEW_KEY : GENERAL_MESSAGES_COLUMNS_VIEW_KEY;

  private getDefaultHiddenColumns = (isMobileView: boolean = this.state.isMobileView): string[] =>
    isMobileView ? [...GENERAL_MESSAGES_MOBILE_HIDDEN_COLUMNS] : [];

  private loadHiddenColumnsForView = (isMobileView: boolean): string[] =>
    loadHiddenColumns(
      this.props.user,
      this.getColumnsViewKey(isMobileView),
      this.getDefaultHiddenColumns(isMobileView)
    ).filter(column => column !== GENERAL_MESSAGES_REQUIRED_COLUMN);

  private setHiddenColumns = (hiddenColumns: string[]): void => {
    const normalizedHiddenColumns = hiddenColumns.filter(column => column !== GENERAL_MESSAGES_REQUIRED_COLUMN);
    saveHiddenColumns(this.props.user, this.getColumnsViewKey(), normalizedHiddenColumns);
    this.setState({ hiddenColumns: normalizedHiddenColumns });
  };

  private resetHiddenColumns = (): void => {
    this.setHiddenColumns(this.getDefaultHiddenColumns());
  };

  private onMobileViewChange = (event: MediaQueryListEvent): void => {
    const isMobileView = event.matches;
    this.setState({
      isMobileView,
      hiddenColumns: this.loadHiddenColumnsForView(isMobileView),
      isColumnsMenuOpen: false
    });
  };

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
    this.mobileViewMediaQuery = window.matchMedia(GENERAL_MESSAGES_MOBILE_MEDIA_QUERY);
    if (this.mobileViewMediaQuery.addEventListener) {
      this.mobileViewMediaQuery.addEventListener('change', this.onMobileViewChange);
    } else {
      this.mobileViewMediaQuery.addListener(this.onMobileViewChange);
    }
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    this.pageHelpRegistration = registerPageHelpDefinition(this.getPageHelpDefinition());
  }

  componentDidUpdate() {
    if (this.pageHelpRegistration) this.pageHelpRegistration.update(this.getPageHelpDefinition());
  }

  componentWillUnmount() {
    if (this.pageHelpRegistration) this.pageHelpRegistration.unregister();
    if (this.mobileViewMediaQuery) {
      if (this.mobileViewMediaQuery.removeEventListener) {
        this.mobileViewMediaQuery.removeEventListener('change', this.onMobileViewChange);
      } else {
        this.mobileViewMediaQuery.removeListener(this.onMobileViewChange);
      }
    }
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  private getPageHelpFilters = (): PageHelpFilter[] => {
    const { appliedSearchText, channelFilter, deliveryFilter, filters, activeStatus } = this.state;
    const result: PageHelpFilter[] = [];
    const createdBy = propz.get(filters, ['createdBy'], '');
    const sender = propz.get(filters, ['sender'], '');
    const createdAt: any = propz.get(filters, ['createdAt'], {}) || {};
    const sentAt: any = propz.get(filters, ['sentAt'], {}) || {};

    if (appliedSearchText.trim()) {
      result.push({ id: 'search', label: 'Search', value: appliedSearchText.trim() });
    }
    if (channelFilter) result.push({ id: 'channel', label: 'Channel', value: channelFilter.toUpperCase() });
    if (deliveryFilter) {
      result.push({
        id: 'delivery',
        label: 'Delivery status',
        value: `${deliveryFilter.charAt(0).toUpperCase()}${deliveryFilter.slice(1)}`
      });
    }
    if (createdBy) result.push({ id: 'createdBy', label: 'Created by', value: createdBy });
    if (createdAt.from || createdAt.to) {
      result.push({
        id: 'createdAt',
        label: 'Created',
        value: `${createdAt.from || 'Any time'} – ${createdAt.to || 'Now'}`
      });
    }
    if (activeStatus === GENERAL_MESSAGE_STATUS.SENT && sender) {
      result.push({ id: 'sender', label: 'Sent by', value: sender });
    }
    if (activeStatus === GENERAL_MESSAGE_STATUS.SENT && (sentAt.from || sentAt.to)) {
      result.push({
        id: 'sentAt',
        label: 'Sent',
        value: `${sentAt.from || 'Any time'} – ${sentAt.to || 'Now'}`
      });
    }
    return result;
  };

  private openPageFilters = (): void => {
    this.setState(prevState => ({
      isShowFilter: true,
      draftFilters: prevState.filters,
      draftChannelFilter: prevState.channelFilter,
      draftDeliveryFilter: prevState.deliveryFilter
    }));
  };

  private getCurrentSentPeriod = (): { start: Date; endExclusive: Date } => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCMonth() >= 7 ? now.getUTCFullYear() : now.getUTCFullYear() - 1, 7, 1));
    return {
      start,
      endExclusive: new Date(Date.UTC(start.getUTCFullYear() + 1, 7, 1))
    };
  };

  private getMessageInteractionHelp = (
    activeStatus: GENERAL_MESSAGE_STATUS_TYPE,
    selectedCount: number
  ): PageHelpObservation[] => {
    const isSent = activeStatus === GENERAL_MESSAGE_STATUS.SENT;
    const isScheduled = activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED;
    const statusLabel = isSent ? 'sent' : isScheduled ? 'scheduled' : 'draft';
    const rowText = isSent
      ? 'Click a message row outside its checkbox to open it without selecting it. You can review the content, recipient categories and delivery totals, then open the full notification list to see each user, channel and delivery status.'
      : isScheduled
      ? 'Click a message row outside its checkbox to open it without selecting it. You can review its content, scheduled time and recipient snapshot, open the full list of people and channels planned for delivery, or copy, reschedule, send now or return the message to draft.'
      : 'Click a message row outside its checkbox to open it without selecting it. You can review its content and current recipient preview before notifications are created, then copy or edit the draft.';
    const observations: PageHelpObservation[] = [
      {
        id: 'row-action',
        tone: 'neutral',
        title: `Click a row to inspect a ${statusLabel} message`,
        text: rowText
      }
    ];

    if (selectedCount === 0) {
      observations.push({
        id: 'checkbox-action',
        tone: 'neutral',
        title: 'Checkboxes select messages for actions',
        text: isSent
          ? 'Select one sent message to create a new message using its title, content and/or recipients. Selecting a checkbox does not open the message.'
          : isScheduled
          ? 'Select one scheduled message to copy it, send it now, change its sending time, return it to draft or delete it. Select several only when you need to delete them together.'
          : 'Select one draft to copy, edit, review and send, or delete it. Select several drafts only when you need to delete them together.'
      });
      return observations;
    }

    if (selectedCount === 1) {
      observations.push({
        id: 'checkbox-selection',
        tone: isSent ? 'neutral' : 'warning',
        title: `One ${statusLabel} message selected`,
        text: isSent
          ? 'You can create a new message like this and choose whether to copy its title, content and/or recipients. The sent message itself will not be changed.'
          : isScheduled
          ? 'You can create a copy, send it now, change its sending time, return it to draft or delete it. Deleting cancels the planned sending and cannot be undone.'
          : 'You can create a copy, edit the draft, review and send it, or delete it. Deletion is permanent and the draft cannot be restored.'
      });
      return observations;
    }

    observations.push({
      id: 'checkbox-selection',
      tone: 'warning',
      title: `${selectedCount.toLocaleString('en-GB')} ${statusLabel} messages selected`,
      text: isSent
        ? 'There are no bulk actions for sent messages. Clear the selection, or keep only one message selected if you want to create a new message like it.'
        : isScheduled
        ? 'The only bulk action is Delete. It permanently removes every selected message and cancels all their planned sending. This cannot be undone.'
        : 'The only bulk action is Delete. It permanently removes every selected draft, and the deleted messages cannot be restored.'
    });
    return observations;
  };

  private getMessageSelectionHelpAnchors = (
    activeStatus: GENERAL_MESSAGE_STATUS_TYPE,
    selectedCount: number
  ): PageHelpAnchor[] => {
    if (selectedCount === 0) return [];

    const isSent = activeStatus === GENERAL_MESSAGE_STATUS.SENT;
    const isScheduled = activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED;
    const statusLabel = isSent ? 'sent' : isScheduled ? 'scheduled' : 'draft';
    const anchors: PageHelpAnchor[] = [
      {
        id: 'gm-selection',
        label: `${selectedCount.toLocaleString('en-GB')} ${statusLabel} ${
          selectedCount === 1 ? 'message' : 'messages'
        } selected`,
        description:
          selectedCount === 1
            ? 'This action bar applies to the selected message.'
            : isSent
            ? 'Sent messages do not have a bulk action. Clear the selection or leave only one selected.'
            : 'Only permanent deletion is available for this multi-selection.'
      }
    ];

    if (selectedCount === 1) {
      anchors.push({
        id: 'gm-selection-copy',
        label: 'Create a new message like this',
        description: 'Choose whether to copy the title, content and/or recipients into a new message.'
      });

      if (activeStatus === GENERAL_MESSAGE_STATUS.DRAFT) {
        anchors.push(
          {
            id: 'gm-selection-edit',
            label: 'Edit draft',
            description: 'Continue changing the selected draft.'
          },
          {
            id: 'gm-selection-review',
            label: 'Review and send',
            description: 'Open the draft in the message flow to review it and choose when to send it.'
          }
        );
      }

      if (isScheduled) {
        anchors.push(
          {
            id: 'gm-selection-return-draft',
            label: 'Return to draft',
            description: 'Cancel the planned sending and make the message editable again.'
          },
          {
            id: 'gm-selection-reschedule',
            label: 'Reschedule',
            description: 'Choose a different date or time for the planned sending.'
          },
          {
            id: 'gm-selection-send-now',
            label: 'Send now',
            description: 'Cancel the wait and send the selected message immediately.'
          }
        );
      }
    }

    if (!isSent) {
      anchors.push({
        id: 'gm-selection-delete',
        label: selectedCount === 1 ? 'Delete message' : `Delete ${selectedCount.toLocaleString('en-GB')} messages`,
        description: isScheduled
          ? 'This permanently deletes the selected message or messages and cancels their planned sending.'
          : 'Be careful: deletion is permanent and the selected draft or drafts cannot be restored.'
      });
    }

    anchors.push({
      id: 'gm-selection-clear',
      label: 'Clear selection',
      description: 'Leave all messages unchanged and close the selection action bar.'
    });
    return anchors;
  };

  private getPageHelpDefinition = (): PageHelpDefinition => {
    if (this.state.isCopyModalOpen && this.state.copySourceMessage) return this.getCopyMessageHelpDefinition();
    if (this.state.scheduledAction && this.state.scheduledActionMessage) return this.getScheduledActionHelpDefinition();
    if (this.state.isDeleteModalOpen) return this.getDeleteMessagesHelpDefinition();
    if (this.state.generalMessage) return this.getMessageDetailsHelpDefinition(this.state.generalMessage);

    const { activeStatus, sentScope, total, selectedItems, currentPage, isLoading, isError, errorMessage } = this.state;
    const statusLabel =
      activeStatus === GENERAL_MESSAGE_STATUS.SENT
        ? 'Sent'
        : activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED
        ? 'Scheduled'
        : 'Drafts';
    const activeView =
      activeStatus === GENERAL_MESSAGE_STATUS.SENT
        ? `${statusLabel} · ${sentScope === 'current' ? 'Current' : 'Archive'}`
        : statusLabel;
    const filters = this.getPageHelpFilters();
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const currentSentPeriod = this.getCurrentSentPeriod();
    const interactionHelp = this.getMessageInteractionHelp(activeStatus, selectedItems.length);
    const selectionAnchors = this.getMessageSelectionHelpAnchors(activeStatus, selectedItems.length);

    return {
      screenId: 'school.general-messages.list',
      title: 'General messages',
      subtitle: 'Contextual help for creating, finding and managing school communications.',
      entity: { singular: 'general message', plural: 'general messages' },
      observations: [
        ...(activeStatus === GENERAL_MESSAGE_STATUS.SENT && sentScope === 'current'
          ? [
              {
                id: 'current-sent-period',
                tone: 'neutral' as const,
                title: 'You are viewing current sent messages',
                text: `Current includes messages sent from 1 August ${currentSentPeriod.start.getUTCFullYear()} to 31 July ${currentSentPeriod.endExclusive.getUTCFullYear()}. The period starts in August because schools often send messages before the academic year begins. For earlier messages, open Archive.`
              }
            ]
          : []),
        ...interactionHelp
      ],
      useDefaultSelectionObservation: false,
      state: {
        loading: isLoading,
        error: isError ? errorMessage || 'Please try loading the page again.' : undefined,
        total,
        visibleCount: this.getVisibleMessages().length,
        selectedCount: selectedItems.length,
        currentPage,
        totalPages: pageCount,
        activeView,
        filters
      },
      actions: [
        {
          id: 'create-message',
          label: 'Create a message',
          description: 'Open the guided flow to choose recipients, channels and content, then send now or schedule it.',
          kind: 'primary',
          anchorId: 'gm-create',
          run: this.onCreateMessageClick
        },
        {
          id: 'open-filters',
          label: 'Open filters',
          description: 'Combine channel, delivery, creator/sender and date criteria to narrow the messages shown.',
          anchorId: 'gm-filters',
          run: this.openPageFilters
        },
        ...(filters.length > 0
          ? [
              {
                id: 'clear-filters',
                label: 'Clear all filters',
                description: 'Remove the search term and every active filter, then show all messages in this tab.',
                run: this.onClearFilterClick
              }
            ]
          : [])
      ],
      anchors: [
        {
          id: 'gm-status-tabs',
          label: 'Message status tabs',
          description:
            'Choose Sent for delivery history, Scheduled for messages waiting to send, or Drafts for unfinished work.'
        },
        {
          id: 'gm-create',
          label: 'Create message',
          description: 'Start a new message and work through audience, content, recipient preview and sending.'
        },
        {
          id: 'gm-search',
          label: 'Search',
          description:
            'Enter part of a message title or a creator/sender name. Pause typing for automatic search, or press Enter to search immediately.'
        },
        {
          id: 'gm-filters',
          label: 'Filters',
          description:
            'Choose criteria, then press Apply or Enter. Results include every matching message, not just this page.'
        },
        {
          id: 'gm-columns',
          label: 'Columns',
          description:
            'Tick the information you want in the table. Desktop and mobile choices are saved separately for this account.'
        },
        ...(activeStatus === GENERAL_MESSAGE_STATUS.SENT
          ? [
              {
                id: 'gm-sent-scope',
                label: 'Current and Archive',
                description:
                  'Current covers 1 August to 31 July around this academic year. Choose Archive when you need older sent messages.'
              }
            ]
          : []),
        {
          id: 'gm-select-control',
          label: 'Select messages',
          description:
            selectedItems.length > 0
              ? 'Open selection options or change which messages are selected.'
              : activeStatus === GENERAL_MESSAGE_STATUS.SENT
              ? 'Select one sent message when you want to create a new message like it.'
              : 'Select one message for individual actions, or several messages for permanent deletion.'
        },
        ...selectionAnchors,
        {
          id: 'gm-results',
          label: 'Message list',
          description:
            'Click a row to inspect the message. Use its checkbox instead when you want to copy, edit, schedule or delete it.'
        },
        {
          id: 'gm-pagination',
          label: 'Pagination',
          description:
            pageCount === 1
              ? 'All matching messages fit on this page, so there is nowhere else to move.'
              : `There are ${pageCount.toLocaleString(
                  'en-GB'
                )} pages. Use the numbered controls, Previous or Next to move through them.`
        }
      ],
      sections: [
        {
          id: 'status-guide',
          title: 'How message statuses work',
          items: [
            'Drafts can be edited and reviewed before any notifications are created.',
            'Scheduled messages can be rescheduled, returned to draft or sent immediately.',
            'Sent messages open delivery details and individual recipient notifications.'
          ]
        },
        {
          id: 'selection-guide',
          title: 'Selecting messages',
          summary: 'Use the checkbox menu to select this page, select all matching results or clear the selection.'
        }
      ]
    };
  };

  private getCopyMessageHelpDefinition = (): PageHelpDefinition => {
    const { copyOptions, copySourceMessage, isCopyPreparing } = this.state;
    const selectedOptions = [
      copyOptions.title ? 'title' : '',
      copyOptions.content ? 'content' : '',
      copyOptions.recipients ? 'recipients' : ''
    ].filter(Boolean);
    const hasSelection = selectedOptions.length > 0;
    return {
      screenId: 'school.general-messages.copy',
      title: 'Create a new message like this',
      subtitle: `Choose what to reuse from “${this.getMessageTitle(copySourceMessage as GeneralMessage)}”.`,
      entity: { singular: 'copy option', plural: 'copy options' },
      observations: [
        {
          id: 'copy-selection',
          tone: hasSelection ? 'positive' : 'warning',
          title: hasSelection ? `Copying ${selectedOptions.join(', ')}` : 'Choose at least one item to copy',
          text: copyOptions.recipients
            ? 'The new wizard will reuse the saved audience and skip its recipient-selection steps. You can still review the resolved audience before sending.'
            : 'Without Recipients, the new message starts with a fresh audience selection.'
        }
      ],
      anchors: [
        {
          id: 'gm-copy-modal',
          label: 'Copy message dialog',
          description: 'Creates a new editable message; the original message is never changed.'
        },
        {
          id: 'gm-copy-options',
          label: 'What to copy',
          description:
            'Title includes the internal description. Content includes enabled channels, text and attachments. Recipients includes the saved audience.'
        },
        {
          id: 'gm-copy-actions',
          label: 'Continue or cancel',
          description: 'Continue opens the normal message wizard with the selected values already filled in.'
        }
      ],
      actions: [
        {
          id: 'continue-copy-message',
          label: 'Continue to message',
          description: 'Open a new message in the full wizard with the selected data copied.',
          kind: 'primary',
          disabled: !hasSelection || isCopyPreparing,
          run: this.createLikeMessage
        },
        {
          id: 'cancel-copy-message',
          label: 'Cancel copying',
          description: 'Close this dialog without creating a new message.',
          disabled: isCopyPreparing,
          run: this.closeCopyModal
        }
      ],
      sections: [
        {
          id: 'copy-options-guide',
          title: 'Choose only what you need',
          items: [
            'Title copies the internal title and description.',
            'Content copies channel settings, message text and email attachments.',
            'Recipients copies the saved selection rules and avoids rebuilding the audience from scratch.'
          ]
        }
      ]
    };
  };

  private getDeleteMessagesHelpDefinition = (): PageHelpDefinition => {
    const selectedCount = this.state.selectedItems.length;
    const scheduledCount = this.state.selectedItems.filter(
      message => message.status === GENERAL_MESSAGE_STATUS.SCHEDULED
    ).length;
    return {
      screenId: 'school.general-messages.delete',
      title: `Delete ${selectedCount === 1 ? 'message' : 'messages'}?`,
      subtitle: 'Squady pauses here because this action cannot be undone.',
      entity: { singular: 'selected message', plural: 'selected messages' },
      observations: [
        {
          id: 'permanent-delete',
          tone: 'danger',
          title: `${selectedCount.toLocaleString('en-GB')} ${
            selectedCount === 1 ? 'message will' : 'messages will'
          } be permanently deleted`,
          text:
            scheduledCount > 0
              ? `${scheduledCount} scheduled ${
                  scheduledCount === 1 ? 'message has' : 'messages have'
                } planned sending that will also be cancelled.`
              : 'Deleted drafts cannot be restored. No sent-message history is deleted through this action.'
        }
      ],
      anchors: [
        {
          id: 'gm-delete-modal',
          label: 'Delete confirmation',
          description: 'Check the number of selected messages carefully before confirming the permanent action.'
        }
      ],
      actions: [
        {
          id: 'cancel-message-delete',
          label: 'Keep the messages',
          description: 'Close the confirmation and leave every selected message unchanged.',
          kind: 'primary',
          run: () => this.setState({ isDeleteModalOpen: false })
        }
      ],
      sections: [
        {
          id: 'delete-warning',
          title: 'Deletion is permanent',
          summary:
            'Use Cancel if the selection is not exactly what you intended. Squady deliberately does not offer the destructive Delete button as a quick help action.'
        }
      ]
    };
  };

  private getScheduledActionHelpDefinition = (): PageHelpDefinition => {
    const {
      scheduledAction,
      scheduledActionMessage,
      scheduledActionAt,
      scheduledActionError,
      isScheduledActionInProgress
    } = this.state;
    const actionLabel =
      scheduledAction === 'send' ? 'Send now' : scheduledAction === 'draft' ? 'Return to draft' : 'Reschedule';
    const isValid =
      scheduledAction !== 'reschedule' || Boolean(scheduledActionAt && scheduledActionAt.getTime() > Date.now());
    return {
      screenId: `school.general-messages.scheduled-action.${scheduledAction}`,
      title: `${actionLabel} · ${this.getMessageTitle(scheduledActionMessage as GeneralMessage)}`,
      subtitle: 'Review exactly how this changes the planned delivery before confirming.',
      entity: { singular: 'scheduled message', plural: 'scheduled messages' },
      observations: [
        {
          id: 'scheduled-change',
          tone: scheduledActionError ? 'danger' : scheduledAction === 'send' ? 'warning' : 'neutral',
          title: scheduledActionError || actionLabel,
          text:
            scheduledAction === 'send'
              ? 'The scheduled time will be removed and delivery will begin immediately after confirmation.'
              : scheduledAction === 'draft'
              ? 'The scheduled time and pending delivery descriptors will be cancelled. The message becomes editable and is not sent.'
              : 'Choose a future date and time. The fixed scheduled audience remains attached to this message.'
        }
      ],
      anchors: [
        {
          id: 'gm-scheduled-action-modal',
          label: `${actionLabel} confirmation`,
          description:
            scheduledAction === 'reschedule'
              ? 'Choose and validate the new future sending time before saving.'
              : 'Confirm this change only when you are ready.'
        }
      ],
      actions: [
        {
          id: 'confirm-scheduled-action',
          label: actionLabel,
          description:
            scheduledAction === 'send'
              ? 'Start delivery immediately.'
              : scheduledAction === 'draft'
              ? 'Cancel planned sending and make the message editable.'
              : 'Save the new sending date and time.',
          kind: 'primary',
          disabled: !isValid || isScheduledActionInProgress,
          run: this.submitScheduledAction
        },
        {
          id: 'cancel-scheduled-action',
          label: 'Cancel',
          description: 'Keep the existing scheduled message unchanged.',
          disabled: isScheduledActionInProgress,
          run: this.closeScheduledActionModal
        }
      ],
      sections: [
        {
          id: 'scheduled-actions-guide',
          title: 'What each option means',
          items: [
            'Send now removes the wait and starts delivery.',
            'Reschedule keeps the message scheduled but changes its future time.',
            'Return to draft cancels planned sending so the message can be edited.'
          ]
        }
      ]
    };
  };

  private getMessageDetailsHelpDefinition = (message: GeneralMessage): PageHelpDefinition => {
    const status = message.status;
    const isSent = status === GENERAL_MESSAGE_STATUS.SENT;
    const isScheduled = status === GENERAL_MESSAGE_STATUS.SCHEDULED;
    const isDraft = status === GENERAL_MESSAGE_STATUS.DRAFT;
    const channels = this.getMessageChannels(message);
    const audienceCount = this.getAudienceCount(message);
    const activeTabLabel =
      this.state.currentTabIndex === 0 ? 'Overview' : this.state.currentTabIndex === 1 ? 'Content' : 'Delivery stats';
    const observations: PageHelpObservation[] = [
      {
        id: 'message-state',
        tone: isSent ? 'positive' : isScheduled ? 'warning' : 'neutral',
        title: isSent
          ? 'This message has been sent'
          : isScheduled
          ? 'This message is scheduled'
          : 'This message is a draft',
        text: isSent
          ? 'The message can no longer be edited. Open Message notifications to inspect every user, channel and delivery status.'
          : isScheduled
          ? 'The audience is fixed for the scheduled send. You can change the time, send now or return it to draft before delivery begins.'
          : 'No notifications have been created. You can inspect the current recipient preview, edit the message or use it as the basis for a new one.'
      },
      {
        id: 'message-audience',
        tone: audienceCount > 0 ? 'positive' : 'warning',
        title:
          audienceCount > 0
            ? `${audienceCount.toLocaleString('en-GB')} users affected`
            : 'No resolved audience count yet',
        text:
          channels.length > 0
            ? `Configured channels: ${channels
                .map(channel => channel.toUpperCase())
                .join(', ')}. The recipient list explains the role that included each person.`
            : 'No delivery channels are currently configured for this message.'
      }
    ];

    return {
      screenId: `school.general-messages.detail.${String(status || 'unknown').toLowerCase()}`,
      title: `Message details · ${this.getMessageTitle(message)}`,
      subtitle: `${activeTabLabel} is open. Squady explains the message without changing it.`,
      entity: { singular: 'message', plural: 'messages' },
      observations,
      anchors: [
        {
          id: 'gm-detail-header',
          label: 'Message summary',
          description:
            'Shows the status, title, recipient categories, parent priorities, enabled channels and content readiness.'
        },
        {
          id: 'gm-detail-tabs',
          label: 'Message detail tabs',
          description: isDraft
            ? 'Switch between Overview and Content. Delivery stats are hidden because a draft has not created notifications.'
            : 'Switch between the message overview, channel content and delivery statistics.'
        },
        {
          id: 'gm-detail-body',
          label: `${activeTabLabel} details`,
          description:
            activeTabLabel === 'Content'
              ? 'Preview the enabled message channels in realistic email and mobile frames.'
              : activeTabLabel === 'Delivery stats'
              ? 'Review aggregate results, then open the full notification list for individual outcomes.'
              : 'Review the audience, timing, creator and recipient setup for this message.'
        },
        {
          id: 'gm-detail-actions',
          label: 'Available message actions',
          description: isSent
            ? 'Create a similar message or inspect individual delivery notifications.'
            : isScheduled
            ? 'Copy, return to draft, reschedule or send the message immediately.'
            : 'Copy or edit this draft before it is sent.'
        }
      ],
      actions: [
        {
          id: 'copy-message',
          label: 'Create message like this',
          description: 'Choose whether to reuse the title, content and/or recipients in a new message.',
          kind: 'primary',
          run: () => this.openCopyModal(message)
        },
        ...(isDraft
          ? [
              {
                id: 'edit-message',
                label: 'Edit draft',
                description: 'Continue through the message wizard with the saved values pre-filled.',
                run: () => this.goToEditMessage(message)
              }
            ]
          : []),
        ...(isScheduled
          ? [
              {
                id: 'reschedule-message',
                label: 'Change sending time',
                description: 'Choose a new future date and time.',
                run: () => this.openScheduledActionModal('reschedule', message)
              },
              {
                id: 'return-message-draft',
                label: 'Return to draft',
                description: 'Cancel scheduled delivery and make the message editable again.',
                run: () => this.openScheduledActionModal('draft', message)
              },
              {
                id: 'send-message-now',
                label: 'Send now',
                description: 'Skip the scheduled time and start sending immediately.',
                run: () => this.sendMessageNow(message)
              }
            ]
          : []),
        ...(!isDraft
          ? [
              {
                id: 'view-message-recipients',
                label: isSent ? 'View message notifications' : 'View scheduled recipients',
                description: isSent
                  ? 'Open the individual user, role, channel and delivery-status list.'
                  : 'Open the fixed recipient and planned-channel list.',
                run: () => this.showNotificationsForMessage(message)
              }
            ]
          : [
              {
                id: 'preview-message-recipients',
                label: 'Preview recipients',
                description: 'Resolve the saved audience and inspect who would receive this draft now.',
                run: () => this.showNotificationsForMessage(message)
              }
            ]),
        {
          id: 'close-message-details',
          label: 'Close message details',
          description: 'Return to the current General messages list without changing the message.',
          run: () => this.setState({ generalMessage: undefined })
        }
      ],
      sections: [
        {
          id: 'message-tabs-guide',
          title: 'What each tab contains',
          items: [
            'Overview explains who the message targets and when it was created, scheduled or sent.',
            'Content shows exactly what each enabled channel contains.',
            ...(isDraft
              ? []
              : ['Delivery stats summarises outcomes; the notification list contains the individual records.'])
          ]
        }
      ]
    };
  };

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.gmColumns')) this.setState({ isColumnsMenuOpen: false });
    if (!target.closest('.gmFilters') && !target.closest('.gmFilterButton')) {
      this.setState(prevState => ({
        isShowFilter: false,
        draftFilters: prevState.filters,
        draftChannelFilter: prevState.channelFilter,
        draftDeliveryFilter: prevState.deliveryFilter
      }));
    }
  };

  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      if (this.state.scheduledAction) {
        if (!this.state.isScheduledActionInProgress) {
          this.closeScheduledActionModal();
        }
        return;
      }
      if (this.state.isCopyModalOpen) {
        if (!this.state.isCopyPreparing) {
          this.closeCopyModal();
        }
        return;
      }
      this.setState(prevState => ({
        isShowFilter: false,
        isColumnsMenuOpen: false,
        generalMessage: undefined,
        draftFilters: prevState.filters,
        draftChannelFilter: prevState.channelFilter,
        draftDeliveryFilter: prevState.deliveryFilter
      }));
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setItems(pageOverride?: number) {
    const { history, user } = this.props;
    const requestVersion = ++this.listRequestVersion;

    const search = parse(history.location.search);
    const page =
      typeof pageOverride === 'number'
        ? pageOverride
        : typeof search.page !== 'undefined'
        ? Number(search.page)
        : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const whereBase = getServerFieldSectionWhere(filters);
    const createdByName = String(propz.get(filters, ['createdBy'], '') || '').trim();
    const senderName = String(propz.get(filters, ['sender'], '') || '').trim();
    delete whereBase.createdBy;
    delete whereBase.sender;
    if (createdByName) whereBase.createdByName = createdByName;
    if (senderName) whereBase.senderName = senderName;
    if (this.state.appliedSearchText.trim() !== '') {
      whereBase.contextSearch = this.state.appliedSearchText.trim();
    }
    const where = {
      ...whereBase,
      status: this.state.activeStatus
    };

    const isSent = this.state.activeStatus === GENERAL_MESSAGE_STATUS.SENT;
    const currentSentPeriod = this.getCurrentSentPeriod();
    const academicYearStart = currentSentPeriod.start;
    const nextAcademicYearStart = currentSentPeriod.endExclusive;
    if (isSent) {
      const requestedSentAt = where.sentAt || {};
      where.sentAt =
        this.state.sentScope === 'archive'
          ? { ...requestedSentAt, $lt: academicYearStart.toISOString() }
          : {
              ...requestedSentAt,
              $gte: requestedSentAt.$gte || academicYearStart.toISOString(),
              $lt: nextAcademicYearStart.toISOString()
            };
    }

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getSchoolGeneralMessages(user, serverQueryFilter);
    const getItemsCountPromise = getSchoolGeneralMessagesCount(user, where);
    const getStatusCountsPromise = getSchoolGeneralMessageStatusCounts(user, whereBase);
    const requestedSentAt = whereBase.sentAt || {};
    const getSentCurrentCountPromise = getSchoolGeneralMessagesCount(user, {
      ...whereBase,
      status: GENERAL_MESSAGE_STATUS.SENT,
      sentAt: {
        ...requestedSentAt,
        $gte: requestedSentAt.$gte || academicYearStart.toISOString(),
        $lt: nextAcademicYearStart.toISOString()
      }
    });
    const getSentArchiveCountPromise = getSchoolGeneralMessagesCount(user, {
      ...whereBase,
      status: GENERAL_MESSAGE_STATUS.SENT,
      sentAt: {
        ...requestedSentAt,
        $lt: academicYearStart.toISOString()
      }
    });
    if (!this.uploadSizeRequest) {
      this.uploadSizeRequest = getSettingsUploadFiles(user).catch(error => {
        this.uploadSizeRequest = undefined;
        throw error;
      });
    }
    const getSettingsUploadFilesPromise = this.uploadSizeRequest;

    const promises = [
      getItemsCountPromise,
      getItemsPromise,
      getSettingsUploadFilesPromise,
      getStatusCountsPromise,
      getSentCurrentCountPromise,
      getSentArchiveCountPromise
    ];

    return BPromise.all(promises)
      .then(([countObj, items, maxSizeUploadFile, statusCounts, sentCurrentCount, sentArchiveCount]) => {
        if (requestVersion !== this.listRequestVersion) return false;
        const total = countObj.count;
        const sentTotal = sentCurrentCount.count + sentArchiveCount.count;
        const message = propz.get(history, ['location', 'state', 'message']);
        if (typeof message !== 'undefined') {
          const state = { ...(history.location.state as any) };
          delete state.message;
          history.replace({ ...history.location, state });

          this.setState({
            isLoading: true
          });

          const messageId = message.id;
          getSchoolGeneralMessageDetails(user, messageId)
            .then(twoPartMessage => {
              const messageEmailSubject = propz.get(twoPartMessage, ['content', 'email', 'subject'], '');
              const messageEmailBody = propz.get(twoPartMessage, ['content', 'email', 'body'], '');
              const messagePushBody = propz.get(twoPartMessage, ['content', 'push', 'body'], '');
              const messageSmsText = propz.get(twoPartMessage, ['content', 'sms', 'text'], '');
              const messagePushTitle = propz.get(twoPartMessage, ['content', 'push', 'title'], '');
              const messageAttachments = propz.get(twoPartMessage, ['content', 'email', 'attachments'], []);

              this.setState(
                {
                  generalMessage: twoPartMessage,
                  selectedItems: [message],
                  sortDirection: sortDirection,
                  sortColumnsName: sortColumnsName,
                  isShowFilter: false,
                  isDataFiltered: isDataFiltered,
                  filters: filters,
                  draftFilters: filters,
                  items: items,
                  total: total,
                  sentTotal,
                  sentCurrentTotal: sentCurrentCount.count,
                  sentArchiveTotal: sentArchiveCount.count,
                  scheduledTotal: statusCounts.SCHEDULED,
                  draftTotal: statusCounts.DRAFT,
                  basePath: history.location.pathname,
                  isLoading: false,

                  messageEmailSubject: messageEmailSubject,
                  messageEmailBody: messageEmailBody,
                  messagePushBody: messagePushBody,
                  messageSmsText: messageSmsText,
                  messagePushTitle: messagePushTitle,
                  messageAttachments: messageAttachments,
                  maxSizeUploadFile: maxSizeUploadFile
                },
                () => this.preloadMessageSummaries(items, requestVersion)
              );
            })
            .catch(() => {
              this.setState(
                {
                  generalMessage: {} as GeneralMessage,
                  selectedItems: [],
                  sortDirection,
                  sortColumnsName,
                  isShowFilter: false,
                  isDataFiltered,
                  filters,
                  draftFilters: filters,
                  items,
                  total,
                  sentTotal,
                  sentCurrentTotal: sentCurrentCount.count,
                  sentArchiveTotal: sentArchiveCount.count,
                  scheduledTotal: statusCounts.SCHEDULED,
                  draftTotal: statusCounts.DRAFT,
                  basePath: history.location.pathname,
                  maxSizeUploadFile,
                  isLoading: false,
                  toastMessage: 'The message no longer exists or is not available to this account.'
                },
                () => this.preloadMessageSummaries(items, requestVersion)
              );
            });
        } else {
          this.setState(
            {
              currentPage: page,
              sortDirection: sortDirection,
              sortColumnsName: sortColumnsName,
              isShowFilter: false,
              isDataFiltered: isDataFiltered,
              filters: filters,
              draftFilters: filters,
              items: items,
              total: total,
              sentTotal,
              sentCurrentTotal: sentCurrentCount.count,
              sentArchiveTotal: sentArchiveCount.count,
              scheduledTotal: statusCounts.SCHEDULED,
              draftTotal: statusCounts.DRAFT,
              basePath: history.location.pathname,
              maxSizeUploadFile: maxSizeUploadFile,
              isLoading: false
            },
            () => this.preloadMessageSummaries(items, requestVersion)
          );
        }

        return true;
      })
      .catch(() => {
        if (requestVersion !== this.listRequestVersion) return false;
        this.setState({
          isLoading: false,
          toastMessage: 'Could not load messages. Please try again.'
        });
        return false;
      });
  }

  private getMessageId(message: GeneralMessage): string {
    return propz.get(message, ['id'], '') || propz.get(message, ['_id'], '');
  }

  private getMessageDetail(messageId: string): Promise<GeneralMessage> {
    if (!this.messageDetailRequests[messageId]) {
      this.messageDetailRequests[messageId] = getSchoolGeneralMessageDetails(this.props.user, messageId)
        .then(message => ({ ...message, __detailLoaded: true }))
        .catch(error => {
          delete this.messageDetailRequests[messageId];
          throw error;
        });
    }

    return this.messageDetailRequests[messageId];
  }

  private hasMessageDetail(message: GeneralMessage): boolean {
    const content = propz.get(message, ['content']);
    return Boolean(message && (message.__detailLoaded || (content && Object.keys(content).length > 0)));
  }

  private preloadMessageSummaries(items: GeneralMessage[], requestVersion: number): void {
    const messageIds = items
      .filter(item => item.status === GENERAL_MESSAGE_STATUS.SENT)
      .map(item => this.getMessageId(item))
      .filter(Boolean);
    if (messageIds.length === 0) return;

    getSchoolGeneralMessageSummaries(this.props.user, messageIds)
      .then(summaries => {
        if (requestVersion !== this.listRequestVersion) return;
        const summariesById = summaries.reduce((result, summary) => {
          result[summary.messageId] = summary;
          return result;
        }, {});

        this.setState(prevState => ({
          items: prevState.items.map(item => {
            const summary = summariesById[this.getMessageId(item)];
            return summary
              ? { ...item, statistics: { ...propz.get(item, ['statistics'], {}), ...summary.statistics } }
              : item;
          })
        }));
      })
      .catch(() => undefined);
  }
  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const { user } = this.props;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      isEditable: false
    });

    if (selectedItemsUpdated.length === 1) {
      this.setState({
        isMessageRefreshing: true
      });

      const messageId = selectedItemsUpdated[0].id;
      getSchoolGeneralMessage(user, messageId).then(message => {
        const messageEmailSubject = propz.get(message, ['content', 'email', 'subject'], '');
        const messageEmailBody = propz.get(message, ['content', 'email', 'body'], '');
        const messagePushBody = propz.get(message, ['content', 'push', 'body'], '');
        const messageSmsText = propz.get(message, ['content', 'sms', 'text'], '');
        const messagePushTitle = propz.get(message, ['content', 'push', 'title'], '');
        const messageAttachments = propz.get(message, ['content', 'email', 'attachments'], []);

        this.setState({
          generalMessage: message,
          messageEmailSubject,
          messageEmailBody,
          messagePushBody,
          messageSmsText,
          messagePushTitle,
          messageAttachments,
          isMessageRefreshing: false
        });
      });
    }
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters, activeStatus, sentScope, appliedSearchText, channelFilter } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);
    const createdByName = String(propz.get(filters, ['createdBy'], '') || '').trim();
    const senderName = String(propz.get(filters, ['sender'], '') || '').trim();
    delete where.createdBy;
    delete where.sender;
    if (createdByName) where.createdByName = createdByName;
    if (senderName) where.senderName = senderName;
    where.status = activeStatus;
    if (activeStatus === GENERAL_MESSAGE_STATUS.SENT) {
      const currentSentPeriod = this.getCurrentSentPeriod();
      const academicYearStart = currentSentPeriod.start;
      const nextAcademicYearStart = currentSentPeriod.endExclusive;
      const requestedSentAt = where.sentAt || {};
      where.sentAt =
        sentScope === 'archive'
          ? { ...requestedSentAt, $lt: academicYearStart.toISOString() }
          : {
              ...requestedSentAt,
              $gte: requestedSentAt.$gte || academicYearStart.toISOString(),
              $lt: nextAcademicYearStart.toISOString()
            };
    }
    if (appliedSearchText.trim()) where.contextSearch = appliedSearchText.trim();

    getAllSchoolGeneralMessages(user, where).then(items => {
      const matchingItems = channelFilter
        ? items.filter(item => this.getMessageChannels(item).indexOf(channelFilter as any) !== -1)
        : items;
      const itemsFiltered = matchingItems.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.draftFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      draftFilters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const {
      draftFilters: filters,
      draftChannelFilter,
      draftDeliveryFilter,
      sortDirection,
      sortColumnsName
    } = this.state;
    this.applyMessageFilters(filters, draftChannelFilter, draftDeliveryFilter, sortDirection, sortColumnsName);
  };

  private applyMessageFilters = (
    filters: any,
    channelFilter: string,
    deliveryFilter: string,
    sortDirection: TABLE_SORT_DIRECTION = this.state.sortDirection,
    sortColumnsName: string = this.state.sortColumnsName
  ): void => {
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      filters,
      draftFilters: filters,
      channelFilter,
      draftChannelFilter: channelFilter,
      deliveryFilter,
      draftDeliveryFilter: deliveryFilter,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  private removeAppliedFilter = (field: string): void => {
    const emptyFilters = getFilters2(COLUMNS, {});
    const nextFilters = {
      ...this.state.filters,
      [field]: emptyFilters[field]
    };
    this.applyMessageFilters(nextFilters, this.state.channelFilter, this.state.deliveryFilter);
  };

  onClearFilterClick = (): void => {
    const emptyFilters = getFilters2(COLUMNS, {});
    const hasLocationState = Boolean(this.props.history.location.search);
    this.setState(
      {
        selectedItems: [],
        isSelectAllChecked: false,
        filters: emptyFilters,
        draftFilters: emptyFilters,
        channelFilter: '',
        draftChannelFilter: '',
        deliveryFilter: '',
        draftDeliveryFilter: '',
        searchText: '',
        appliedSearchText: '',
        currentPage: FIRST_PAGE,
        isLoading: true,
        isShowFilter: false
      },
      () => {
        if (!hasLocationState) this.setItems(FIRST_PAGE);
      }
    );

    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onCreateMessageClick = (): void => {
    const { history } = this.props;

    this.props.history.push({
      pathname: `/generalMessages/create`,
      state: {
        isClearPage: true,
        isCreateLikeThis: false,
        search: history.location.search
      }
    });
  };

  isOneMessageSelected() {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  onDeleteMessageClick = () => {
    const { selectedItems } = this.state;
    if (!this.isDeleteMessageActive()) {
      this.setState({
        isDeleteModalOpen: false,
        toastMessage: 'Only draft or scheduled messages can be deleted'
      });
      return;
    }

    const { user } = this.props;
    this.setState({
      isLoading: true
    });

    BPromise.all(
      selectedItems.map(message => {
        return deleteSchoolGeneralMessage(user, message.id);
      })
    ).then(res => {
      this.setState({
        selectedItems: [],
        isSelectAllChecked: false,
        generalMessage: undefined,
        isDeleteModalOpen: false,
        toastMessage: 'Messages deleted'
      });
      this.setItems();
    });
  };

  isDeleteMessageActive(): boolean {
    const { selectedItems } = this.state;

    const isEveryMessageDeletable = selectedItems.every(
      message => message.status === GENERAL_MESSAGE_STATUS.DRAFT || message.status === GENERAL_MESSAGE_STATUS.SCHEDULED
    );
    return selectedItems.length > 0 && isEveryMessageDeletable;
  }

  private icon(name: string): React.ReactNode {
    const paths = {
      search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3',
      plus: 'M12 5v14M5 12h14',
      sms: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
      email: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M22 7l-10 6L2 7',
      push: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
      users:
        'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
      clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 6v6l4 2',
      edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
      copy:
        'M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2V9zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
      trash: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
      send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
      close: 'M18 6 6 18M6 6l12 12',
      eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
      alert: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
      filter: 'M3 5h18M6 12h12M10 19h4',
      columns: 'M4 5h16v14H4zM10 5v14M16 5v14',
      refresh: 'M20 11a8 8 0 1 0 2 5.3M20 4v7h-7'
    };

    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={paths[name]} />
      </svg>
    );
  }

  private stripHtml(value: string): string {
    let decoded = String(value || '');

    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      for (let i = 0; i < 3; i++) {
        textarea.innerHTML = decoded;
        const next = textarea.value;
        if (next === decoded) break;
        decoded = next;
      }
    }

    const withoutServiceBlocks = decoded
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<head[\s\S]*?<\/head>/gi, ' ')
      .replace(/<svg[\s\S]*?<\/svg>/gi, ' ')
      .replace(/<div>\s*Attachments:[\s\S]*$/gi, ' ')
      .replace(/Attachments:\s*$/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/tr>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\r/g, '');

    return withoutServiceBlocks
      .split('\n')
      .map(line => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n')
      .trim();
  }
  private formatMessageDate(value?: string): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private getMessageTitle(message: GeneralMessage): string {
    return (
      propz.get(message, ['title'], '') || propz.get(message, ['content', 'email', 'subject'], '') || 'Untitled message'
    );
  }

  private getMessageEmailSubject(message: GeneralMessage): string {
    const title = propz.get(message, ['title'], '');
    const subject = propz.get(message, ['emailSubject'], '') || propz.get(message, ['content', 'email', 'subject'], '');
    return title && subject ? subject : '';
  }

  private getMessageSenderName(message: GeneralMessage): string {
    return `${propz.get(message, ['senderData', 'firstName'], '')} ${propz.get(
      message,
      ['senderData', 'lastName'],
      ''
    )}`.trim();
  }

  private getMessagePreview(message: GeneralMessage): string {
    return (
      propz.get(message, ['description'], '') ||
      propz.get(message, ['content', 'sms', 'text'], '') ||
      propz.get(message, ['content', 'push', 'body'], '') ||
      this.stripHtml(propz.get(message, ['content', 'email', 'body'], '')) ||
      'No preview available'
    );
  }

  private getMessageChannels(message: GeneralMessage): Array<'sms' | 'email' | 'push'> {
    const channels: Array<'sms' | 'email' | 'push'> = [];
    const deliveryChannels = propz.get(message, ['deliveryChannels']);
    if (deliveryChannels && ['sms', 'email', 'push'].some(channel => typeof deliveryChannels[channel] === 'boolean')) {
      if (deliveryChannels.sms) channels.push('sms');
      if (deliveryChannels.email) channels.push('email');
      if (deliveryChannels.push) channels.push('push');
      return channels.length > 0 ? channels : ['email'];
    }
    const smsText = propz.get(message, ['content', 'sms', 'text'], '');
    const emailSubject = propz.get(message, ['content', 'email', 'subject'], '');
    const emailBody = propz.get(message, ['content', 'email', 'body'], '');
    const pushTitle = propz.get(message, ['content', 'push', 'title'], '');
    const pushBody = propz.get(message, ['content', 'push', 'body'], '');

    if (smsText) channels.push('sms');
    if (emailSubject || emailBody) channels.push('email');
    if (pushTitle || pushBody) channels.push('push');

    return channels.length > 0 ? channels : ['email'];
  }

  private toPositiveNumber(value: any): number {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    return typeof numericValue === 'number' && !isNaN(numericValue) && numericValue > 0 ? numericValue : 0;
  }

  private getAudienceCountFromMessage(message: GeneralMessage): number {
    const affectedUserList = propz.get(message, ['affectedUserList'], []);
    if (Array.isArray(affectedUserList) && affectedUserList.length > 0) {
      const uniqueUserIds = new Set<string>();
      affectedUserList.forEach((affectedUser: any) => {
        const userId =
          propz.get(affectedUser, ['userId']) || propz.get(affectedUser, ['id']) || propz.get(affectedUser, ['_id']);
        if (userId) uniqueUserIds.add(String(userId));
      });
      if (uniqueUserIds.size > 0) return uniqueUserIds.size;
    }

    const countCandidates = [
      propz.get(message, ['affectedUsersCount']),
      propz.get(message, ['affectedUserCount']),
      propz.get(message, ['affectedUsers', 'count']),
      propz.get(message, ['affectedUsers', 'total']),
      propz.get(message, ['affectedUsers', 'length']),
      propz.get(message, ['affectedUserListCount']),
      propz.get(message, ['recipientListCount']),
      propz.get(message, ['recipientsCount']),
      propz.get(message, ['recipientCount']),
      propz.get(message, ['recipients', 'count']),
      propz.get(message, ['recipients', 'total']),
      propz.get(message, ['audienceCount']),
      propz.get(message, ['statistics', 'affectedUsersCount']),
      propz.get(message, ['statistics', 'recipientsCount'])
    ];

    const numericCount = countCandidates.map(value => this.toPositiveNumber(value)).find(value => value > 0);
    if (numericCount) return numericCount;

    const arrayCandidates = [
      propz.get(message, ['affectedUserList'], []),
      propz.get(message, ['affectedUsers'], []),
      propz.get(message, ['recipients'], []),
      propz.get(message, ['recipientList'], [])
    ];
    const firstArray = arrayCandidates.find(value => Array.isArray(value) && value.length > 0);
    if (Array.isArray(firstArray)) return firstArray.length;

    const students = propz.get(message, ['subscribers', 'lists', 'students'], []);
    const staffs = propz.get(message, ['subscribers', 'lists', 'staffs'], []);
    const governors = propz.get(message, ['subscribers', 'lists', 'governors'], []);
    const baseCount =
      (Array.isArray(students) ? students.length : this.toPositiveNumber(students)) +
      (Array.isArray(staffs) ? staffs.length : this.toPositiveNumber(staffs)) +
      (Array.isArray(governors) ? governors.length : this.toPositiveNumber(governors));

    return baseCount;
  }

  private getAudienceCount(message: GeneralMessage): number {
    const countFromMessage = this.getAudienceCountFromMessage(message);
    if (countFromMessage > 0) return countFromMessage;

    const id = this.getMessageId(message);
    return id ? this.state.audienceCountById[id] || 0 : 0;
  }
  private getMessageStats(
    message: GeneralMessage
  ): { delivered: number; failed: number; unavailable: number; sent: number } {
    const statistics: any = propz.get(message, ['statistics'], {});
    // The summary chart is user-based. Channel statistics describe individual
    // delivery attempts and must not be added to the notification totals.
    const notification = statistics.notification || {};
    const read = Number(notification.READ || 0);
    const delivered = Number(notification.DELIVERED || 0) + read;
    const sentOnly = Number(notification.SENT || 0);
    const stats = {
      delivered,
      failed: Number(notification.ERROR || 0),
      unavailable:
        Number(notification.NO_CHANNELS || 0) +
        Number(notification.NOT_SENT || 0) +
        Number(notification.UNSUPPORTED_CHANNEL_TYPE || 0),
      sent: delivered + sentOnly
    };

    const audience = this.getAudienceCount(message);
    if (stats.sent === 0 && message.status === GENERAL_MESSAGE_STATUS.SENT && audience > 0) {
      stats.sent = audience;
    }

    return stats;
  }

  private getDeliveryStats(message: GeneralMessage, channel: 'overview' | 'sms' | 'email' | 'push') {
    const statistics: any = propz.get(message, ['statistics'], {});
    const enabledChannels = this.getMessageChannels(message);
    const source =
      channel === 'overview'
        ? enabledChannels.length === 1
          ? statistics[enabledChannels[0]] || statistics.notification || {}
          : statistics.notification || {}
        : statistics[channel] || {};
    const delivered = Number(source.DELIVERED || 0) + Number(source.READ || 0);
    const sent = Number(source.SENT || 0);
    const failed = Number(source.ERROR || 0);
    const unavailable =
      Number(source.NO_CHANNELS || 0) + Number(source.NOT_SENT || 0) + Number(source.UNSUPPORTED_CHANNEL_TYPE || 0);
    const processing = Number(source.SCHEDULED || 0) + Number(source.SENDING || 0);
    const calculatedTotal = delivered + sent + failed + unavailable + processing;
    const total =
      channel === 'overview'
        ? Math.max(this.getAudienceCount(message), calculatedTotal)
        : Math.max(Number(source.TOTAL || 0), calculatedTotal);
    const pending = Math.max(processing, total - delivered - sent - failed - unavailable);

    return { delivered, sent, failed, unavailable, pending, total };
  }
  private getVisibleMessages(): GeneralMessage[] {
    const { items, appliedSearchText, channelFilter, deliveryFilter, filters } = this.state;
    const query = appliedSearchText.trim().toLowerCase();
    const createdByQuery = String(propz.get(filters, ['createdBy'], '') || '')
      .trim()
      .toLowerCase();
    const senderQuery = String(propz.get(filters, ['sender'], '') || '')
      .trim()
      .toLowerCase();
    const matchesPersonName = (name: string, personQuery: string): boolean =>
      personQuery === '' ||
      personQuery
        .split(/\s+/)
        .filter(Boolean)
        .every(token => name.toLowerCase().indexOf(token) !== -1);

    return items.filter(message => {
      const channels = this.getMessageChannels(message);
      const matchesChannel = channelFilter === '' || channels.indexOf(channelFilter as any) !== -1;
      const text = [
        this.getMessageTitle(message),
        this.getMessagePreview(message),
        this.getMessageSenderName(message),
        propz.get(message, ['createdBy', 'firstName'], ''),
        propz.get(message, ['createdBy', 'lastName'], '')
      ]
        .join(' ')
        .toLowerCase();
      const matchesSearch = query === '' || text.indexOf(query) !== -1;
      const createdByName = `${propz.get(message, ['createdBy', 'firstName'], '')} ${propz.get(
        message,
        ['createdBy', 'lastName'],
        ''
      )}`.trim();
      const senderName = `${this.getMessageSenderName(message)} ${propz.get(
        message,
        ['senderDisplayName'],
        ''
      )}`.trim();
      const matchesCreatedBy = matchesPersonName(createdByName, createdByQuery);
      const matchesSender = matchesPersonName(senderName, senderQuery);
      const stats = this.getMessageStats(message);
      const sent = Math.max(stats.sent, this.getAudienceCount(message), 1);
      const matchesDelivery =
        deliveryFilter === '' ||
        (deliveryFilter === 'delivered' && stats.delivered >= sent) ||
        (deliveryFilter === 'failed' && stats.failed > 0) ||
        (deliveryFilter === 'unavailable' && stats.unavailable > 0) ||
        (deliveryFilter === 'pending' && stats.delivered < sent && stats.failed === 0 && stats.unavailable === 0);

      return matchesChannel && matchesSearch && matchesCreatedBy && matchesSender && matchesDelivery;
    });
  }

  private getCurrentStatusLabel(): string {
    switch (this.state.activeStatus) {
      case GENERAL_MESSAGE_STATUS.SCHEDULED:
        return 'Scheduled';
      case GENERAL_MESSAGE_STATUS.DRAFT:
        return 'Drafts';
      default:
        return 'Sent';
    }
  }

  private onStatusTabClick = (status: GENERAL_MESSAGE_STATUS_TYPE): void => {
    if (this.state.activeStatus === status) return;

    this.setState(
      {
        activeStatus: status,
        selectedItems: [],
        generalMessage: undefined,
        currentPage: FIRST_PAGE,
        channelFilter: '',
        draftChannelFilter: '',
        deliveryFilter: '',
        draftDeliveryFilter: '',
        isSelectAllChecked: false,
        isLoading: true
      },
      () => this.setItems()
    );
  };

  private onSentScopeClick = (sentScope: 'current' | 'archive'): void => {
    if (this.state.sentScope === sentScope) return;

    this.setState(
      {
        sentScope,
        currentPage: FIRST_PAGE,
        selectedItems: [],
        generalMessage: undefined,
        isSelectAllChecked: false,
        isLoading: true
      },
      () => this.setItems()
    );
  };

  private onSearchTextChange = (searchText: string): void => {
    this.setState({ searchText });
  };

  private onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.setState(
      state => ({ appliedSearchText: state.searchText, currentPage: FIRST_PAGE, isLoading: true }),
      () => this.setItems(FIRST_PAGE)
    );
  };

  private clearSearchText = (): void => {
    if (!this.state.searchText && !this.state.appliedSearchText) return;
    this.setState(
      { searchText: '', appliedSearchText: '', currentPage: FIRST_PAGE, isLoading: true },
      () => this.setItems(FIRST_PAGE)
    );
  };

  private onMessageRowClick = (message: GeneralMessage): void => {
    const firstChannel = this.getMessageChannels(message)[0];
    const messageId = this.getMessageId(message);
    const hasDetail = this.hasMessageDetail(message);
    const shouldRefreshDetail = !hasDetail || message.status === GENERAL_MESSAGE_STATUS.SENT;

    this.setState({
      generalMessage: message,
      isMessageRefreshing: shouldRefreshDetail,
      drawerChannel: firstChannel,
      deliveryStatsChannel: 'overview',
      currentTabIndex: 0
    });

    if (!shouldRefreshDetail) return;

    this.getMessageDetail(messageId)
      .then(twoPartMessage => {
        if (this.getMessageId(this.state.generalMessage) !== messageId) return;

        const messageEmailSubject = propz.get(twoPartMessage, ['content', 'email', 'subject'], '');
        const messageEmailBody = propz.get(twoPartMessage, ['content', 'email', 'body'], '');
        const messagePushBody = propz.get(twoPartMessage, ['content', 'push', 'body'], '');
        const messageSmsText = propz.get(twoPartMessage, ['content', 'sms', 'text'], '');
        const messagePushTitle = propz.get(twoPartMessage, ['content', 'push', 'title'], '');
        const messageAttachments = propz.get(twoPartMessage, ['content', 'email', 'attachments'], []);
        const audienceCount = this.getAudienceCountFromMessage(twoPartMessage);
        const nextMessageId = this.getMessageId(twoPartMessage) || messageId;

        this.setState({
          generalMessage: twoPartMessage,
          messageEmailSubject,
          messageEmailBody,
          messagePushBody,
          messageSmsText,
          messagePushTitle,
          messageAttachments,
          isMessageRefreshing: false,
          audienceCountById: {
            ...this.state.audienceCountById,
            [nextMessageId]: audienceCount
          }
        });
      })
      .catch(() => {
        if (this.getMessageId(this.state.generalMessage) !== messageId) return;
        this.setState({
          isMessageRefreshing: false,
          toastMessage: 'Could not load the message. Please try again.'
        });
      });
  };

  private refreshDrawerDeliveryStats = (): void => {
    const { generalMessage } = this.state;
    const messageId = this.getMessageId(generalMessage);
    if (!messageId || this.state.isMessageRefreshing) return;

    this.setState({ isMessageRefreshing: true });
    getSchoolGeneralMessageDetails(this.props.user, messageId)
      .then(message => {
        const audienceCount = this.getAudienceCountFromMessage(message);
        this.setState(prevState => ({
          generalMessage: message,
          isMessageRefreshing: false,
          audienceCountById:
            audienceCount > 0
              ? { ...prevState.audienceCountById, [messageId]: audienceCount }
              : prevState.audienceCountById
        }));
      })
      .catch(() => {
        this.setState({
          isMessageRefreshing: false,
          toastMessage: 'Could not refresh delivery statistics. Please try again.'
        });
      });
  };

  private isMessageSelected(message: GeneralMessage): boolean {
    return this.state.selectedItems.some(item => item.id === message.id);
  }

  private onMessageCheckboxClick = (event, message: GeneralMessage): void => {
    event.stopPropagation();
    const isSelected = this.isMessageSelected(message);
    const selectedItems = isSelected
      ? this.state.selectedItems.filter(item => item.id !== message.id)
      : [...this.state.selectedItems, message];

    this.setState({ selectedItems, isSelectAllChecked: selectedItems.length === this.getVisibleMessages().length });
  };

  private onSelectVisibleClick = (): void => {
    const visible = this.getVisibleMessages();
    this.setState(prevState => {
      const selectedIds = new Set(prevState.selectedItems.map(item => item.id));
      const selectedItems = [...prevState.selectedItems, ...visible.filter(item => !selectedIds.has(item.id))];
      return { selectedItems, isSelectAllChecked: visible.length > 0 };
    });
  };

  private goToEditMessage = (message?: GeneralMessage): void => {
    const target = message || this.state.selectedItems[0] || this.state.generalMessage;
    if (!target) return;

    this.props.history.push({
      pathname: `/generalMessages/${target.id}/edit`,
      state: {
        search: this.props.history.location.search,
        user: this.props.user,
        generalMessage:
          this.state.generalMessage && this.state.generalMessage.id === target.id ? this.state.generalMessage : target
      }
    });
  };

  private openCopyModal = (message?: GeneralMessage): void => {
    const target = message || this.state.selectedItems[0] || this.state.generalMessage;
    if (!target) return;

    this.setState({
      isCopyModalOpen: true,
      isCopyPreparing: false,
      copySourceMessage: target,
      generalMessage: undefined,
      copyOptions: {
        title: true,
        content: true,
        recipients: false
      }
    });
  };

  private closeCopyModal = (): void => {
    if (this.state.isCopyPreparing) return;
    this.setState({ isCopyModalOpen: false, copySourceMessage: undefined });
  };

  private onCopyOptionChange = (option: keyof GeneralMessageCopyOptions): void => {
    this.setState(prevState => ({
      copyOptions: {
        ...prevState.copyOptions,
        [option]: !prevState.copyOptions[option]
      }
    }));
  };

  private createLikeMessage = (): void => {
    const { copySourceMessage, copyOptions } = this.state;
    const messageId = copySourceMessage ? this.getMessageId(copySourceMessage) : '';
    if (!messageId || (!copyOptions.title && !copyOptions.content && !copyOptions.recipients)) return;

    this.setState({ isCopyPreparing: true, toastMessage: '' });
    getSchoolGeneralMessageForEditor(this.props.user, messageId)
      .then(sourceMessage => {
        this.props.history.push({
          pathname: `/generalMessages/create`,
          state: {
            search: this.props.history.location.search,
            sourceMessage,
            isCreateLikeThis: true,
            copyOptions
          }
        });
      })
      .catch(error => {
        this.setState({
          isCopyPreparing: false,
          toastMessage: getGeneralMessageRequestError(error, 'The message could not be prepared for copying.')
        });
      });
  };

  private sendMessageNow = (message?: GeneralMessage): void => {
    const target = message || this.state.selectedItems[0] || this.state.generalMessage;
    if (!target) return;

    if (target.status === GENERAL_MESSAGE_STATUS.SCHEDULED) {
      this.openScheduledActionModal('send', target);
      return;
    }

    this.setState({ isLoading: true });
    sendSchoolGeneralMessage(this.props.user, target.id)
      .then(() => {
        this.setState({ selectedItems: [], generalMessage: undefined, toastMessage: 'Message sent' });
        this.setItems();
      })
      .catch(error => {
        this.setState({
          isLoading: false,
          toastMessage: getGeneralMessageRequestError(error, 'The message could not be sent.')
        });
      });
  };

  private openScheduledActionModal = (action: 'send' | 'reschedule' | 'draft', message?: GeneralMessage): void => {
    const target = message || this.state.selectedItems[0] || this.state.generalMessage;
    if (!target || target.status !== GENERAL_MESSAGE_STATUS.SCHEDULED) return;

    const currentDate = target.scheduleAt ? new Date(target.scheduleAt) : null;
    this.setState({
      scheduledAction: action,
      scheduledActionMessage: target,
      scheduledActionAt: currentDate && !Number.isNaN(currentDate.getTime()) ? currentDate : null,
      scheduledActionError: '',
      isScheduledActionInProgress: false,
      generalMessage: undefined
    });
  };

  private closeScheduledActionModal = (): void => {
    if (this.state.isScheduledActionInProgress) return;
    this.setState({
      scheduledAction: null,
      scheduledActionMessage: undefined,
      scheduledActionAt: null,
      scheduledActionError: ''
    });
  };

  private submitScheduledAction = (): void => {
    const { scheduledAction, scheduledActionMessage, scheduledActionAt } = this.state;
    if (!scheduledAction || !scheduledActionMessage) return;

    if (scheduledAction === 'reschedule' && (!scheduledActionAt || scheduledActionAt.getTime() <= Date.now())) {
      this.setState({ scheduledActionError: 'Choose a date and time in the future.' });
      return;
    }

    this.setState({ isScheduledActionInProgress: true, scheduledActionError: '' });
    const request =
      scheduledAction === 'send'
        ? sendSchoolGeneralMessage(this.props.user, scheduledActionMessage.id)
        : scheduledAction === 'draft'
        ? returnSchoolGeneralMessageToDraft(this.props.user, scheduledActionMessage.id)
        : scheduleSchoolGeneralMessage(this.props.user, scheduledActionMessage.id, scheduledActionAt.toISOString());

    request
      .then(() => {
        const toastMessage =
          scheduledAction === 'send'
            ? 'Message queued to send now'
            : scheduledAction === 'draft'
            ? 'Message returned to draft'
            : 'Message rescheduled';
        this.setState({
          scheduledAction: null,
          scheduledActionMessage: undefined,
          scheduledActionAt: null,
          scheduledActionError: '',
          isScheduledActionInProgress: false,
          selectedItems: [],
          isSelectAllChecked: false,
          generalMessage: undefined,
          toastMessage
        });
        this.setItems();
      })
      .catch(error => {
        this.setState({
          isScheduledActionInProgress: false,
          scheduledActionError: getGeneralMessageRequestError(
            error,
            scheduledAction === 'send'
              ? 'The message could not be sent now.'
              : scheduledAction === 'draft'
              ? 'The message could not be returned to draft.'
              : 'The message could not be rescheduled.'
          )
        });
      });
  };

  private showNotificationsForMessage = (message?: GeneralMessage): void => {
    const target = message || this.state.selectedItems[0] || this.state.generalMessage;
    if (!target) return;

    this.props.history.push({
      pathname: `/generalMessages/${target.id}/notifications`,
      search: target.status === GENERAL_MESSAGE_STATUS.SENT ? '' : '?view=recipient-preview',
      state: {
        returnSearch: this.props.history.location.search
      }
    });
  };

  private renderChannelPills(message: GeneralMessage): React.ReactNode {
    const channels = this.getMessageChannels(message);
    return (
      <div className="gmChannels">
        {(['email', 'sms', 'push'] as Array<'sms' | 'email' | 'push'>).map(channel => (
          <span
            key={channel}
            className={channels.indexOf(channel) !== -1 ? `gmChannel gmChannel--${channel}` : 'gmChannel'}
          >
            {this.icon(channel)}
          </span>
        ))}
      </div>
    );
  }

  private renderAudience(message: GeneralMessage): React.ReactNode {
    const count = this.getAudienceCount(message);

    return (
      <div className="gmAudience">
        <strong>{count}</strong>
      </div>
    );
  }

  private renderDelivery(message: GeneralMessage): React.ReactNode {
    if (message.status !== GENERAL_MESSAGE_STATUS.SENT) {
      return (
        <span className={`gmStatusBadge gmStatusBadge--${message.status.toLowerCase()}`}>
          {this.getCurrentStatusLabel()}
        </span>
      );
    }

    const notificationStatistics = propz.get(message, ['statistics', 'notification']);
    if (!notificationStatistics) {
      return <span className="gmDeliveryLoading">Loading delivery…</span>;
    }

    const stats = this.getMessageStats(message);
    const sent = Math.max(stats.sent, this.getAudienceCount(message), 1);
    const deliveredPercent = Math.min(100, Math.round((stats.delivered / sent) * 100));

    return (
      <div className="gmDelivery">
        <div className="gmDeliveryTrack">
          <span className="gmDeliveryDelivered" style={{ width: `${deliveredPercent}%` }} />
        </div>
        <div className="gmDeliveryMeta">
          <span>
            {stats.delivered}/{sent} delivered ({deliveredPercent}%)
          </span>
          {stats.failed > 0 && <em>{stats.failed} failed</em>}
        </div>
      </div>
    );
  }
  renderMessagesTable(): React.ReactNode {
    const {
      activeStatus,
      sentTotal,
      sentCurrentTotal,
      sentArchiveTotal,
      scheduledTotal,
      draftTotal,
      selectedItems,
      currentPage,
      total,
      searchText,
      appliedSearchText,
      channelFilter
    } = this.state;
    const hiddenColumns = new Set(this.state.hiddenColumns);
    const createdAtFilter: any = propz.get(this.state.draftFilters, ['createdAt'], {}) || {};
    const sentAtFilter: any = propz.get(this.state.draftFilters, ['sentAt'], {}) || {};
    const activeFilterChips: Array<{ key: string; label: string }> = [];
    const appliedCreatedBy = propz.get(this.state.filters, ['createdBy'], '');
    const appliedCreatedAt: any = propz.get(this.state.filters, ['createdAt'], {}) || {};
    const appliedSender = propz.get(this.state.filters, ['sender'], '');
    const appliedSentAt: any = propz.get(this.state.filters, ['sentAt'], {}) || {};
    if (appliedSearchText.trim()) {
      activeFilterChips.push({ key: 'search', label: `Search: ${appliedSearchText.trim()}` });
    }
    if (channelFilter) activeFilterChips.push({ key: 'channel', label: `Channel: ${channelFilter.toUpperCase()}` });
    if (this.state.deliveryFilter) {
      activeFilterChips.push({
        key: 'delivery',
        label: `Status: ${this.state.deliveryFilter.charAt(0).toUpperCase() + this.state.deliveryFilter.slice(1)}`
      });
    }
    if (appliedCreatedBy) activeFilterChips.push({ key: 'createdBy', label: `Created by: ${appliedCreatedBy}` });
    if (appliedCreatedAt.from || appliedCreatedAt.to) {
      activeFilterChips.push({
        key: 'createdAt',
        label: `Created: ${appliedCreatedAt.from || '…'} — ${appliedCreatedAt.to || '…'}`
      });
    }
    if (activeStatus === GENERAL_MESSAGE_STATUS.SENT && appliedSender) {
      activeFilterChips.push({ key: 'sender', label: `Sent by: ${appliedSender}` });
    }
    if (activeStatus === GENERAL_MESSAGE_STATUS.SENT && (appliedSentAt.from || appliedSentAt.to)) {
      activeFilterChips.push({
        key: 'sentAt',
        label: `Sent: ${appliedSentAt.from || '…'} — ${appliedSentAt.to || '…'}`
      });
    }
    const messageColumns = [
      { key: 'message', label: 'Message' },
      { key: 'channels', label: 'Channels' },
      { key: 'audience', label: 'Audience' },
      { key: 'status', label: activeStatus === GENERAL_MESSAGE_STATUS.SENT ? 'Delivery' : 'Status' },
      { key: 'created', label: 'Created' },
      ...(activeStatus === GENERAL_MESSAGE_STATUS.SENT ? [{ key: 'sent', label: 'Sent' }] : []),
      ...(activeStatus !== GENERAL_MESSAGE_STATUS.SENT
        ? [
            {
              key: 'date',
              label: activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED ? 'Scheduled' : 'Last edited'
            }
          ]
        : [])
    ];
    const visibleColumnCount = messageColumns.filter(column => !hiddenColumns.has(column.key)).length;
    const visibleMessages = this.getVisibleMessages();
    const isAllVisibleSelected =
      visibleMessages.length > 0 && visibleMessages.every(message => this.isMessageSelected(message));
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const pageStart = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const pageEnd = Math.min(pageCount, pageStart + 4);
    const pages = Array.from({ length: pageEnd - pageStart + 1 }, (_, index) => pageStart + index);
    return (
      <div className="gmPage">
        <header className="gmPageHeader">
          <h1>General messages</h1>
          <nav
            className="gmTabs"
            role="tablist"
            aria-label="General message statuses"
            data-help-anchor="gm-status-tabs"
          >
            <button
              type="button"
              className={activeStatus === GENERAL_MESSAGE_STATUS.SENT ? 'is-active' : ''}
              onClick={() => this.onStatusTabClick(GENERAL_MESSAGE_STATUS.SENT as GENERAL_MESSAGE_STATUS_TYPE)}
            >
              Sent <span>{sentTotal}</span>
            </button>
            <button
              type="button"
              className={activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED ? 'is-active' : ''}
              onClick={() => this.onStatusTabClick(GENERAL_MESSAGE_STATUS.SCHEDULED as GENERAL_MESSAGE_STATUS_TYPE)}
            >
              Scheduled <span>{scheduledTotal}</span>
            </button>
            <button
              type="button"
              className={activeStatus === GENERAL_MESSAGE_STATUS.DRAFT ? 'is-active' : ''}
              onClick={() => this.onStatusTabClick(GENERAL_MESSAGE_STATUS.DRAFT as GENERAL_MESSAGE_STATUS_TYPE)}
            >
              Drafts <span>{draftTotal}</span>
            </button>
          </nav>
          <button
            type="button"
            className="gmButton gmButton--primary"
            onClick={this.onCreateMessageClick}
            data-help-anchor="gm-create"
          >
            <span className="gmCreateIcon" aria-hidden="true">
              +
            </span>{' '}
            Create message
          </button>
        </header>

        <div className="gmShell">
          <section className="gmListCard">
            <div className="gmToolbar">
              <label className="gmSearch" data-help-anchor="gm-search">
                {this.icon('search')}
                <input
                  type="text"
                  value={searchText}
                  placeholder="Search by title or sender…"
                  onChange={event => this.onSearchTextChange(event.target.value)}
                  onKeyDown={this.onSearchKeyDown}
                />
                {searchText && (
                  <button
                    type="button"
                    className="gmSearch__clear"
                    onClick={this.clearSearchText}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </label>
              <button
                type="button"
                className={`gmFilterButton ${this.state.isShowFilter ? 'is-active' : ''}`}
                onClick={() =>
                  this.setState(prevState => ({
                    isShowFilter: !prevState.isShowFilter,
                    draftFilters: prevState.filters,
                    draftChannelFilter: prevState.channelFilter,
                    draftDeliveryFilter: prevState.deliveryFilter
                  }))
                }
                aria-label="Filters"
                data-help-anchor="gm-filters"
              >
                {this.icon('filter')}
                Filters
                {activeFilterChips.length > 0 && <b>{activeFilterChips.length}</b>}
              </button>
              <div className="gmColumns" data-help-anchor="gm-columns">
                <button
                  type="button"
                  className="gmButton gmButton--secondary gmColumnsButton"
                  onClick={() => this.setState({ isColumnsMenuOpen: !this.state.isColumnsMenuOpen })}
                >
                  {this.icon('columns')} Columns <span>{visibleColumnCount}</span>
                </button>
                {this.state.isColumnsMenuOpen && (
                  <div className="gmColumnsMenu">
                    <div>
                      <strong>{this.state.isMobileView ? 'Mobile columns' : 'Desktop columns'}</strong>
                      <button type="button" onClick={this.resetHiddenColumns}>
                        Reset
                      </button>
                    </div>
                    {messageColumns.map(column => (
                      <label
                        key={column.key}
                        className={column.key === GENERAL_MESSAGES_REQUIRED_COLUMN ? 'is-required' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(column.key)}
                          disabled={column.key === GENERAL_MESSAGES_REQUIRED_COLUMN}
                          onChange={() =>
                            this.setHiddenColumns(
                              hiddenColumns.has(column.key)
                                ? this.state.hiddenColumns.filter(key => key !== column.key)
                                : [...this.state.hiddenColumns, column.key]
                            )
                          }
                        />
                        {column.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="gmToolbarSpacer" />
              {activeStatus === GENERAL_MESSAGE_STATUS.SENT && (
                <div
                  className="gmSentScopes"
                  role="tablist"
                  aria-label="Sent message period"
                  data-help-anchor="gm-sent-scope"
                >
                  <button
                    type="button"
                    className={this.state.sentScope === 'current' ? 'is-active' : ''}
                    onClick={() => this.onSentScopeClick('current')}
                  >
                    Current <span>{sentCurrentTotal}</span>
                  </button>
                  <button
                    type="button"
                    className={this.state.sentScope === 'archive' ? 'is-active' : ''}
                    onClick={() => this.onSentScopeClick('archive')}
                  >
                    Archive <span>{sentArchiveTotal}</span>
                  </button>
                </div>
              )}
            </div>

            {this.state.isShowFilter && (
              <form
                className="gmFilters"
                role="dialog"
                aria-label="Message filters"
                onSubmit={event => {
                  event.preventDefault();
                  this.onApplyFilterClick();
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !(event.target as HTMLElement).closest('.sit-dp__pop')) {
                    event.preventDefault();
                    this.onApplyFilterClick();
                  }
                }}
              >
                <div className="gmFiltersHeader">
                  <strong>Filters</strong>
                  <button
                    type="button"
                    aria-label="Close filters"
                    onClick={() =>
                      this.setState(prevState => ({
                        isShowFilter: false,
                        draftFilters: prevState.filters,
                        draftChannelFilter: prevState.channelFilter,
                        draftDeliveryFilter: prevState.deliveryFilter
                      }))
                    }
                  >
                    ×
                  </button>
                </div>
                <div className="gmFiltersBody">
                  <fieldset className="gmFilterChoices">
                    <legend>Channel</legend>
                    <div>
                      {[
                        { value: 'email', label: 'Email' },
                        { value: 'sms', label: 'SMS' },
                        { value: 'push', label: 'Push' }
                      ].map(option => (
                        <button
                          key={option.value}
                          type="button"
                          className={this.state.draftChannelFilter === option.value ? 'is-active' : ''}
                          onClick={() =>
                            this.setState({
                              draftChannelFilter: this.state.draftChannelFilter === option.value ? '' : option.value
                            })
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  {activeStatus === GENERAL_MESSAGE_STATUS.SENT && (
                    <fieldset className="gmFilterChoices">
                      <legend>Delivery status</legend>
                      <div>
                        {['delivered', 'unavailable', 'failed', 'pending'].map(status => (
                          <button
                            key={status}
                            type="button"
                            className={this.state.draftDeliveryFilter === status ? 'is-active' : ''}
                            onClick={() =>
                              this.setState({
                                draftDeliveryFilter: this.state.draftDeliveryFilter === status ? '' : status
                              })
                            }
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  <label>
                    <span>Created by</span>
                    <input
                      className="gmInput"
                      value={this.state.draftFilters.createdBy || ''}
                      placeholder="Name or surname"
                      onChange={event => this.onTableFilterChange(event, 'createdBy')}
                    />
                  </label>
                  <div className="gmFilterDateField">
                    <span>Created at</span>
                    <SitDatePicker
                      className="gmDateRange"
                      mode="range"
                      value={{
                        start: createdAtFilter.from ? moment(createdAtFilter.from, 'DD-MM-YYYY').toDate() : null,
                        end: createdAtFilter.to ? moment(createdAtFilter.to, 'DD-MM-YYYY').toDate() : null
                      }}
                      onChange={(range: any) =>
                        this.onTableFilterChange(
                          {
                            target: {
                              value: {
                                from: range && range.start ? moment(range.start).format('DD-MM-YYYY') : '',
                                to: range && range.end ? moment(range.end).format('DD-MM-YYYY') : ''
                              }
                            }
                          },
                          'createdAt'
                        )
                      }
                      format="DD-MM-YYYY"
                      numberOfMonths={2}
                      placeholder="Select date range"
                    />
                  </div>
                  {activeStatus === GENERAL_MESSAGE_STATUS.SENT && (
                    <>
                      <label>
                        <span>Sent by</span>
                        <input
                          className="gmInput"
                          value={this.state.draftFilters.sender || ''}
                          placeholder="Name or surname"
                          onChange={event => this.onTableFilterChange(event, 'sender')}
                        />
                      </label>
                      <div className="gmFilterDateField">
                        <span>Sent at</span>
                        <SitDatePicker
                          className="gmDateRange"
                          mode="range"
                          value={{
                            start: sentAtFilter.from ? moment(sentAtFilter.from, 'DD-MM-YYYY').toDate() : null,
                            end: sentAtFilter.to ? moment(sentAtFilter.to, 'DD-MM-YYYY').toDate() : null
                          }}
                          onChange={(range: any) =>
                            this.onTableFilterChange(
                              {
                                target: {
                                  value: {
                                    from: range && range.start ? moment(range.start).format('DD-MM-YYYY') : '',
                                    to: range && range.end ? moment(range.end).format('DD-MM-YYYY') : ''
                                  }
                                }
                              },
                              'sentAt'
                            )
                          }
                          format="DD-MM-YYYY"
                          numberOfMonths={2}
                          placeholder="Select date range"
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="gmFiltersActions">
                  <button type="button" className="gmFilterClear is-clear" onClick={this.onClearFilterClick}>
                    Clear all
                  </button>
                  <div>
                    <button
                      type="button"
                      className="gmButton gmButton--secondary"
                      onClick={() =>
                        this.setState(prevState => ({
                          isShowFilter: false,
                          draftFilters: prevState.filters,
                          draftChannelFilter: prevState.channelFilter,
                          draftDeliveryFilter: prevState.deliveryFilter
                        }))
                      }
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="gmButton gmButton--primary is-apply"
                      onClick={this.onApplyFilterClick}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </form>
            )}

            {activeFilterChips.length > 0 && (
              <div className="gmFilterChips" aria-label="Active filters">
                {activeFilterChips.map(chip => (
                  <span className="gmFilterChip" key={chip.key}>
                    {chip.label}
                    <button
                      type="button"
                      aria-label={`Remove ${chip.label}`}
                      onClick={() =>
                        chip.key === 'channel'
                          ? this.applyMessageFilters(this.state.filters, '', this.state.deliveryFilter)
                          : chip.key === 'delivery'
                          ? this.applyMessageFilters(this.state.filters, this.state.channelFilter, '')
                          : chip.key === 'search'
                          ? this.clearSearchText()
                          : this.removeAppliedFilter(chip.key)
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button type="button" className="gmFilterChips__clear" onClick={this.onClearFilterClick}>
                  Clear all
                </button>
              </div>
            )}

            {selectedItems.length > 0 && (
              <div className="gmSelectionBar" data-help-anchor="gm-selection">
                <strong>{selectedItems.length} selected</strong>
                {selectedItems.length === 1 && (
                  <>
                    <button
                      type="button"
                      className="gmButton gmButton--secondary"
                      onClick={() => this.openCopyModal()}
                      data-help-anchor="gm-selection-copy"
                    >
                      {this.icon('copy')} Create a new message like this
                    </button>
                  </>
                )}
                {selectedItems.length === 1 && activeStatus === GENERAL_MESSAGE_STATUS.DRAFT && (
                  <button
                    type="button"
                    className="gmButton gmButton--secondary"
                    onClick={() => this.goToEditMessage()}
                    data-help-anchor="gm-selection-edit"
                  >
                    Edit
                  </button>
                )}
                {activeStatus === GENERAL_MESSAGE_STATUS.DRAFT && selectedItems.length === 1 && (
                  <button
                    type="button"
                    className="gmButton gmButton--primary"
                    onClick={() => this.goToEditMessage()}
                    data-help-anchor="gm-selection-review"
                  >
                    Review &amp; send
                  </button>
                )}
                {activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED && selectedItems.length === 1 && (
                  <>
                    <button
                      type="button"
                      className="gmButton gmButton--secondary"
                      onClick={() => this.openScheduledActionModal('draft')}
                      data-help-anchor="gm-selection-return-draft"
                    >
                      Return to draft
                    </button>
                    <button
                      type="button"
                      className="gmButton gmButton--secondary"
                      onClick={() => this.openScheduledActionModal('reschedule')}
                      data-help-anchor="gm-selection-reschedule"
                    >
                      {this.icon('clock')} Reschedule
                    </button>
                    <button
                      type="button"
                      className="gmButton gmButton--primary"
                      onClick={() => this.sendMessageNow()}
                      data-help-anchor="gm-selection-send-now"
                    >
                      {this.icon('send')} Send now
                    </button>
                  </>
                )}
                {this.isDeleteMessageActive() && (
                  <button
                    type="button"
                    className="gmButton gmButton--danger"
                    onClick={() => this.setState({ isDeleteModalOpen: true })}
                    data-help-anchor="gm-selection-delete"
                  >
                    Delete
                  </button>
                )}
                <div className="gmToolbarSpacer" />
                <button
                  type="button"
                  className="gmSelectionClear"
                  onClick={this.onUnselectAllClick}
                  data-help-anchor="gm-selection-clear"
                >
                  Clear selection
                </button>
              </div>
            )}

            <div className="gmTableCard" data-help-anchor="gm-results">
              <table
                className="gmTable"
                style={
                  this.state.isMobileView
                    ? ({ minWidth: `${Math.max(360, 84 + visibleColumnCount * 145)}px` } as React.CSSProperties)
                    : undefined
                }
              >
                <thead>
                  <tr>
                    <th className="gmSelectCell" data-help-anchor="gm-select-control">
                      <SelectAllCheckbox
                        compact={true}
                        onSelectAllClick={this.onSelectAllClick}
                        onSelectAllOnPageClick={this.onSelectVisibleClick}
                        onUnselectAllClick={this.onUnselectAllClick}
                        isChecked={isAllVisibleSelected}
                        totalCount={total}
                        currentPage={currentPage}
                        selectedItems={selectedItems}
                        pageItemsCount={visibleMessages.length}
                      />
                    </th>
                    {!hiddenColumns.has('message') && <th>Message</th>}
                    {!hiddenColumns.has('channels') && <th>Channels</th>}
                    {!hiddenColumns.has('audience') && <th>Audience</th>}
                    {!hiddenColumns.has('status') && (
                      <th>{activeStatus === GENERAL_MESSAGE_STATUS.SENT ? 'Delivery' : 'Status'}</th>
                    )}
                    {!hiddenColumns.has('created') && <th>Created</th>}
                    {activeStatus === GENERAL_MESSAGE_STATUS.SENT && !hiddenColumns.has('sent') && <th>Sent</th>}
                    {activeStatus !== GENERAL_MESSAGE_STATUS.SENT && !hiddenColumns.has('date') && (
                      <th>
                        {activeStatus === GENERAL_MESSAGE_STATUS.SENT
                          ? 'Sent'
                          : activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED
                          ? 'Scheduled'
                          : 'Last edited'}
                      </th>
                    )}
                    <th className="gmRowOpenCell" aria-label="Open message" />
                  </tr>
                </thead>
                <tbody>
                  {visibleMessages.map(message => (
                    <tr
                      key={message.id}
                      className={[
                        this.state.generalMessage && this.state.generalMessage.id === message.id ? 'is-open' : '',
                        this.isMessageSelected(message) ? 'is-selected' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => this.onMessageRowClick(message)}
                    >
                      <td className="gmSelectCell" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={this.isMessageSelected(message)}
                          onChange={event => this.onMessageCheckboxClick(event, message)}
                        />
                      </td>
                      {!hiddenColumns.has('message') && (
                        <td>
                          <div className="gmMessageCell">
                            <strong>{this.getMessageTitle(message)}</strong>
                            {this.getMessageEmailSubject(message) && (
                              <span>{this.getMessageEmailSubject(message)}</span>
                            )}
                          </div>
                        </td>
                      )}
                      {!hiddenColumns.has('channels') && <td>{this.renderChannelPills(message)}</td>}
                      {!hiddenColumns.has('audience') && <td>{this.renderAudience(message)}</td>}
                      {!hiddenColumns.has('status') && <td>{this.renderDelivery(message)}</td>}
                      {!hiddenColumns.has('created') && (
                        <td>
                          <div className="gmDateCell">
                            <strong>
                              {`${propz.get(message, ['createdBy', 'firstName'], '')} ${propz.get(
                                message,
                                ['createdBy', 'lastName'],
                                ''
                              )}`.trim() || '—'}
                            </strong>
                            <span>{this.formatMessageDate(message.createdAt)}</span>
                          </div>
                        </td>
                      )}
                      {activeStatus === GENERAL_MESSAGE_STATUS.SENT && !hiddenColumns.has('sent') && (
                        <td>
                          <div className="gmDateCell">
                            <strong>{getGeneralMessageSender(message) || '—'}</strong>
                            <span>{this.formatMessageDate(message.sentAt)}</span>
                          </div>
                        </td>
                      )}
                      {activeStatus !== GENERAL_MESSAGE_STATUS.SENT && !hiddenColumns.has('date') && (
                        <td>
                          <div className="gmDateCell">
                            <strong>
                              {activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED
                                ? message.scheduleAt
                                  ? this.formatMessageDate(message.scheduleAt)
                                  : 'Schedule time unavailable'
                                : this.formatMessageDate(message.updatedAt || message.createdAt)}
                            </strong>
                            <span>
                              {activeStatus === GENERAL_MESSAGE_STATUS.SCHEDULED
                                ? `Scheduled by ${this.getMessageSenderName(message) || '—'}`
                                : `${propz.get(message, ['createdBy', 'firstName'], '')} ${propz.get(
                                    message,
                                    ['createdBy', 'lastName'],
                                    ''
                                  )}`.trim()}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="gmRowOpenCell" aria-hidden="true">
                        <span>›</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleMessages.length === 0 && (
                <div className="gmEmpty">
                  {this.icon('eye')}
                  <h2>No messages found</h2>
                  <p>Try another tab, channel or search term.</p>
                </div>
              )}
            </div>

            <div className="gmFooter" data-help-anchor="gm-pagination">
              <span>
                Page {Math.min(currentPage, pageCount)} of {pageCount}
              </span>
              <div className="gmFooter__pages">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => this.setCurrentPageParams(currentPage - 1)}
                >
                  Previous
                </button>
                {pageStart > 1 && (
                  <>
                    <button type="button" onClick={() => this.setCurrentPageParams(1)}>
                      1
                    </button>
                    {pageStart > 2 && <i>…</i>}
                  </>
                )}
                {pages.map(page => (
                  <button
                    type="button"
                    key={page}
                    className={page === currentPage ? 'is-active' : ''}
                    onClick={() => this.setCurrentPageParams(page)}
                  >
                    {page}
                  </button>
                ))}
                {pageEnd < pageCount && (
                  <>
                    {pageEnd < pageCount - 1 && <i>…</i>}
                    <button type="button" onClick={() => this.setCurrentPageParams(pageCount)}>
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage >= pageCount}
                  onClick={() => this.setCurrentPageParams(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        </div>
        {this.renderMessageDrawer()}
        {this.renderCopyModal()}
        {this.renderDeleteModal()}
        {this.renderScheduledActionModal()}
        {this.state.toastMessage && <div className="gmToast">{this.state.toastMessage}</div>}
      </div>
    );
  }

  private renderMessageDrawer(): React.ReactNode {
    const { generalMessage, drawerChannel, deliveryStatsChannel, isMessageRefreshing } = this.state;
    if (!generalMessage) return null;

    const channels = this.getMessageChannels(generalMessage);
    const activeChannel = channels.indexOf(drawerChannel) !== -1 ? drawerChannel : channels[0];
    const audience = this.getAudienceCount(generalMessage);
    const stats = this.getMessageStats(generalMessage);
    const availableDeliveryStatsChannels: Array<'overview' | 'sms' | 'email' | 'push'> = ['overview', ...channels];
    const activeDeliveryStatsChannel =
      availableDeliveryStatsChannels.indexOf(deliveryStatsChannel) !== -1 ? deliveryStatsChannel : 'overview';
    const deliveryStats = this.getDeliveryStats(generalMessage, activeDeliveryStatsChannel);
    const smsText = this.stripHtml(propz.get(generalMessage, ['content', 'sms', 'text'], ''));
    const emailSubject = this.stripHtml(propz.get(generalMessage, ['content', 'email', 'subject'], ''));
    const emailBody = this.stripHtml(propz.get(generalMessage, ['content', 'email', 'body'], ''));
    const pushTitle = this.stripHtml(propz.get(generalMessage, ['content', 'push', 'title'], ''));
    const pushBody = this.stripHtml(propz.get(generalMessage, ['content', 'push', 'body'], ''));
    const createdBy = `${propz.get(generalMessage, ['createdBy', 'firstName'], '')} ${propz.get(
      generalMessage,
      ['createdBy', 'lastName'],
      ''
    )}`.trim();
    const sentBy = this.getMessageSenderName(generalMessage);
    const isSent = generalMessage.status === GENERAL_MESSAGE_STATUS.SENT;
    const isScheduled = generalMessage.status === GENERAL_MESSAGE_STATUS.SCHEDULED;
    const isDraft = generalMessage.status === GENERAL_MESSAGE_STATUS.DRAFT;
    const overviewStats = this.getDeliveryStats(generalMessage, 'overview');
    const sentCount = overviewStats.sent + overviewStats.delivered;
    const pendingCount = overviewStats.pending;
    const totalForChart = Math.max(deliveryStats.total, 1);
    const deliveredDeg = Math.round((deliveryStats.delivered / totalForChart) * 360);
    const sentDeg = Math.round((deliveryStats.sent / totalForChart) * 360);
    const failedDeg = Math.round((deliveryStats.failed / totalForChart) * 360);
    const unavailableDeg = Math.round((deliveryStats.unavailable / totalForChart) * 360);
    const deliveryPercent = (value: number): number => Math.round((value / totalForChart) * 100);
    const subscribers: any = propz.get(generalMessage, ['subscribers'], {});
    const subscriberTypes: any = propz.get(subscribers, ['types'], {});
    const parentSettings: any = propz.get(subscribers, ['parentsDeliverySettings'], {});
    const studentsCount = propz.get(subscribers, ['lists', 'students', 'length'], 0);
    const staffCount = propz.get(subscribers, ['lists', 'staffs', 'length'], 0);
    const recipientSelection: any = propz.get(generalMessage, ['recipientSelection'], {});
    const studentsSelection: any = propz.get(recipientSelection, ['students'], {});
    const staffSelection: any = propz.get(recipientSelection, ['staffs'], {});
    const governorsSelection: any = propz.get(recipientSelection, ['governors'], {});
    const getSelectionDescription = (group: any, legacyCount: number): string => {
      if (group.mode === 'FILTER') {
        const excludedCount = Array.isArray(group.excluded) ? group.excluded.length : 0;
        return excludedCount > 0
          ? `Selected using filters · ${excludedCount} manually excluded`
          : 'Selected using saved filters';
      }
      const selectedCount = Array.isArray(group.selected) ? group.selected.length : legacyCount;
      return selectedCount > 0
        ? `${selectedCount} ${selectedCount === 1 ? 'person' : 'people'} selected manually`
        : 'Included through the saved audience settings';
    };
    const parentPriorities = [
      parentSettings.all ? 'All parent priorities' : '',
      parentSettings.firstPriority ? '1st priority' : '',
      parentSettings.secondPriority ? '2nd priority' : '',
      parentSettings.thirdPriority ? '3rd priority' : ''
    ].filter(Boolean);
    const affectedUsers = propz.get(generalMessage, ['affectedUserList'], []);
    const resolvedParentIds = Array.isArray(affectedUsers)
      ? affectedUsers.reduce((ids: string[], affectedUser: any) => {
          const permissions = propz.get(affectedUser, ['permissions'], []);
          const isParent =
            propz.get(affectedUser, ['extra', 'type']) === 'PARENT' ||
            (Array.isArray(permissions) &&
              permissions.some(permission => propz.get(permission, ['preset']) === 'PARENT'));
          const userId = String(
            propz.get(affectedUser, ['userId']) ||
              propz.get(affectedUser, ['id']) ||
              propz.get(affectedUser, ['_id']) ||
              ''
          );
          return isParent && userId && ids.indexOf(userId) === -1 ? [...ids, userId] : ids;
        }, [])
      : [];
    const hasSelection = (selection: any): boolean =>
      selection &&
      (selection.mode === 'FILTER' ||
        (Array.isArray(selection.selected) && selection.selected.length > 0) ||
        Number(selection.matchingCount || 0) > 0);
    const hasStructuredRecipientSelection = Boolean(recipientSelection && Object.keys(recipientSelection).length > 0);
    const areStudentsSelected = Boolean(subscriberTypes.students || hasSelection(studentsSelection));
    const areStaffSelected = Boolean(
      hasStructuredRecipientSelection ? hasSelection(staffSelection) : subscriberTypes.schoolStaff
    );
    const areGovernorsSelected = hasSelection(governorsSelection);
    const recipientCategories = [
      areStudentsSelected ? 'Students' : '',
      subscriberTypes.parents ? 'Parents' : '',
      areStaffSelected ? 'Staff' : '',
      areGovernorsSelected ? 'Trustees / governors' : ''
    ].filter(Boolean);
    const isChannelContentReady = (channel: 'sms' | 'email' | 'push'): boolean => {
      switch (channel) {
        case 'sms':
          return Boolean(smsText.trim());
        case 'push':
          return Boolean(pushTitle.trim() && pushBody.trim());
        default:
          return Boolean(emailSubject.trim() && emailBody.trim());
      }
    };
    const readyChannelCount = channels.filter(isChannelContentReady).length;
    const isContentComplete = readyChannelCount === channels.length;
    const tabs: Array<{ key: 'summary' | 'delivery' | 'content' | 'audience'; label: string }> = [
      { key: 'summary', label: 'Summary' },
      ...(isSent ? [{ key: 'delivery' as const, label: 'Delivery stats' }] : []),
      { key: 'content', label: 'Content' },
      {
        key: 'audience',
        label: isScheduled ? 'Scheduled recipients' : isDraft ? 'Recipient preview' : 'Users affected'
      }
    ];
    const activeTab = tabs[this.state.currentTabIndex] || tabs[0];

    return (
      <div className="gmDetailsBackdrop" onMouseDown={() => this.setState({ generalMessage: undefined })}>
        <section className="gmDetailsModal" onMouseDown={event => event.stopPropagation()}>
          <header className="gmDetailsHeader" data-help-anchor="gm-detail-header">
            <div>
              <span className={`gmStatusBadge gmStatusBadge--${generalMessage.status.toLowerCase()}`}>
                {generalMessage.status.toLowerCase()}
              </span>
              <h2>{this.getMessageTitle(generalMessage)}</h2>
              {emailSubject && emailSubject !== this.getMessageTitle(generalMessage) && <p>{emailSubject}</p>}
              <div className="gmDetailsHeaderChips" aria-label="Message setup">
                {recipientCategories.map(category => (
                  <span className="gmDetailsHeaderChip is-recipient" key={`recipient-${category}`}>
                    {category}
                  </span>
                ))}
                {subscriberTypes.parents && parentPriorities.length > 0 && (
                  <span className="gmDetailsHeaderChip is-priority">Parents: {parentPriorities.join(', ')}</span>
                )}
                {channels.map(channel => (
                  <span className="gmDetailsHeaderChip is-channel" key={`channel-${channel}`}>
                    {this.icon(channel)}{' '}
                    {channel === 'sms' ? 'SMS' : channel.charAt(0).toUpperCase() + channel.slice(1)}
                  </span>
                ))}
                <span className={`gmDetailsHeaderChip ${isContentComplete ? 'is-ready' : 'is-incomplete'}`}>
                  {isContentComplete
                    ? 'Content ready'
                    : readyChannelCount > 0
                    ? `${readyChannelCount} of ${channels.length} channels ready`
                    : 'Content not added'}
                </span>
              </div>
            </div>
            <button type="button" className="gmIconButton" onClick={() => this.setState({ generalMessage: undefined })}>
              {this.icon('close')}
            </button>
          </header>

          <nav className="gmDetailsTabs" data-help-anchor="gm-detail-tabs">
            {tabs.map((tab, index) => (
              <button
                key={tab.key}
                type="button"
                className={this.state.currentTabIndex === index ? 'is-active' : ''}
                onClick={() => this.setState({ currentTabIndex: index })}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="gmDetailsBody" data-help-anchor="gm-detail-body">
            {isMessageRefreshing ? (
              <Loader />
            ) : (
              <>
                {activeTab.key === 'summary' && (
                  <div className="gmDetailsSummary">
                    <div className="gmMetricChips">
                      <span>
                        Audience <b>{audience}</b>
                      </span>
                      {isSent && (
                        <span>
                          Sent <b>{sentCount}</b>
                        </span>
                      )}
                      {isSent && (
                        <span className="is-success">
                          Delivered <b>{stats.delivered}</b>
                        </span>
                      )}
                      {isSent && (
                        <span className="is-danger">
                          Failed <b>{stats.failed}</b>
                        </span>
                      )}
                      {isSent && stats.unavailable > 0 && (
                        <span>
                          Unavailable <b>{stats.unavailable}</b>
                        </span>
                      )}
                      {isScheduled && (
                        <span>
                          Scheduled <b>{this.formatMessageDate(generalMessage.scheduleAt)}</b>
                        </span>
                      )}
                      {pendingCount > 0 && (
                        <span>
                          {isScheduled ? 'Awaiting delivery' : 'Pending'} <b>{pendingCount}</b>
                        </span>
                      )}
                    </div>
                    <div className="gmDetailsGrid">
                      <div>
                        <span>Created by</span>
                        <strong>{createdBy || '—'}</strong>
                      </div>
                      <div>
                        <span>Created at</span>
                        <strong>{this.formatMessageDate(generalMessage.createdAt)}</strong>
                      </div>
                      <div>
                        <span>{isScheduled ? 'Scheduled by' : 'Sent by'}</span>
                        <strong>{isSent || isScheduled ? sentBy || '—' : '—'}</strong>
                      </div>
                      <div>
                        <span>{isScheduled ? 'Scheduled for' : 'Sent at'}</span>
                        <strong>
                          {isScheduled
                            ? this.formatMessageDate(generalMessage.scheduleAt)
                            : this.formatMessageDate(generalMessage.sentAt)}
                        </strong>
                      </div>
                      <div>
                        <span>Channels</span>
                        <strong>{channels.map(channel => channel.toUpperCase()).join(', ')}</strong>
                      </div>
                      <div>
                        <span>Status</span>
                        <strong>{generalMessage.status.toLowerCase()}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab.key === 'delivery' && (
                  <div className="gmDeliveryStats">
                    <div className="gmDeliveryStatsToolbar">
                      <div className="gmDeliveryChannelTabs">
                        {availableDeliveryStatsChannels.map(channel => (
                          <button
                            key={channel}
                            type="button"
                            className={activeDeliveryStatsChannel === channel ? 'is-active' : ''}
                            onClick={() => this.setState({ deliveryStatsChannel: channel })}
                          >
                            {channel === 'overview' ? 'Overview' : channel.toUpperCase()}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="gmDeliveryRefresh"
                        onClick={this.refreshDrawerDeliveryStats}
                        title="Refresh delivery statistics"
                      >
                        {this.icon('refresh')}
                        Refresh
                      </button>
                    </div>
                    <div className="gmDeliveryChart">
                      <div
                        className="gmDonut"
                        style={{
                          background: `conic-gradient(#35a66f 0 ${deliveredDeg}deg, #3478c5 ${deliveredDeg}deg ${deliveredDeg +
                            sentDeg}deg, #d6455d ${deliveredDeg + sentDeg}deg ${deliveredDeg +
                            sentDeg +
                            failedDeg}deg, #9aa8b5 ${deliveredDeg + sentDeg + failedDeg}deg ${deliveredDeg +
                            sentDeg +
                            failedDeg +
                            unavailableDeg}deg, #dce5ec ${deliveredDeg +
                            sentDeg +
                            failedDeg +
                            unavailableDeg}deg 360deg)`
                        }}
                      >
                        <div>
                          <strong>{deliveryStats.total}</strong>
                          <span>Total</span>
                        </div>
                      </div>
                      <div className="gmDonutLegend">
                        <span>
                          <i className="is-delivered" />
                          Delivered{' '}
                          <b>
                            {deliveryStats.delivered} · {deliveryPercent(deliveryStats.delivered)}%
                          </b>
                        </span>
                        <span>
                          <i className="is-sent" />
                          Sent{' '}
                          <b>
                            {deliveryStats.sent} · {deliveryPercent(deliveryStats.sent)}%
                          </b>
                        </span>
                        <span>
                          <i className="is-failed" />
                          Failed{' '}
                          <b>
                            {deliveryStats.failed} · {deliveryPercent(deliveryStats.failed)}%
                          </b>
                        </span>
                        <span>
                          <i className="is-unavailable" />
                          Unavailable{' '}
                          <b>
                            {deliveryStats.unavailable} · {deliveryPercent(deliveryStats.unavailable)}%
                          </b>
                        </span>
                        <span>
                          <i className="is-pending" />
                          Pending{' '}
                          <b>
                            {deliveryStats.pending} · {deliveryPercent(deliveryStats.pending)}%
                          </b>
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab.key === 'content' && (
                  <div className="gmContentTab">
                    <div className="gmDrawerChannels">
                      {channels.map(channel => (
                        <button
                          key={channel}
                          type="button"
                          className={activeChannel === channel ? 'is-active' : ''}
                          onClick={() => this.setState({ drawerChannel: channel })}
                        >
                          {this.icon(channel)}{' '}
                          {channel === 'sms' ? 'SMS' : channel.charAt(0).toUpperCase() + channel.slice(1)}
                        </button>
                      ))}
                    </div>
                    {activeChannel === 'email' ? (
                      <div className="gmLaptop">
                        <div className="gmLaptopLid">
                          <span className="gmLaptopCamera" />
                          <div className="gmLaptopBar">
                            <div className="gmLaptopWindowControls" aria-hidden="true">
                              <i />
                              <i />
                              <i />
                            </div>
                            <span>Message preview</span>
                          </div>
                          <div className="gmLaptopScreen">
                            <article className="gmEmailMockup">
                              <header>
                                <span className="gmEmailMockupLogo">S</span>
                                <div>
                                  <strong>Squad In Touch</strong>
                                  <span>Message to recipient</span>
                                </div>
                              </header>
                              <div className="gmEmailMockupBody">
                                <span className="gmPreviewEyebrow">Email subject</span>
                                <h3>{emailSubject || 'No email subject yet'}</h3>
                                {emailBody ? (
                                  <p>{emailBody}</p>
                                ) : (
                                  <div className="gmPreviewEmpty">Email content has not been added yet.</div>
                                )}
                              </div>
                            </article>
                          </div>
                        </div>
                        <div className="gmLaptopBase">
                          <span />
                        </div>
                      </div>
                    ) : (
                      <div className="gmPhoneFrame">
                        <div className={`gmPhoneScreen gmPhoneScreen--${activeChannel}`}>
                          <div className="gmPhoneStatusBar">
                            <span>9:41</span>
                            <div aria-hidden="true">
                              <i />
                              <i />
                              <i />
                            </div>
                          </div>
                          {activeChannel === 'sms' ? (
                            <>
                              <div className="gmPhoneTop">Squad In Touch</div>
                              <span className="gmConversationDate">Today, 9:41</span>
                              <div className={`gmSmsBubble ${smsText ? '' : 'is-empty'}`}>
                                {smsText || 'SMS content has not been added yet.'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="gmPushClock">
                                9:41<span>Monday, 26 August</span>
                              </div>
                              <div className="gmPushPreview">
                                <div className="gmPushAppIcon">{this.icon('push')}</div>
                                <div>
                                  <span>Squad In Touch</span>
                                  <strong>{pushTitle || 'No push title yet'}</strong>
                                  <p>{pushBody || 'Push notification content has not been added yet.'}</p>
                                </div>
                              </div>
                            </>
                          )}
                          <span className="gmPhoneHomeIndicator" aria-hidden="true" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab.key === 'audience' && (
                  <div className="gmAffected">
                    <div className="gmAffectedTotal">
                      <span>{isSent ? 'Recipients' : 'Expected recipients'}</span>
                      <strong>{audience}</strong>
                      <em>
                        {isSent
                          ? 'People included when this message was sent'
                          : isScheduled
                          ? 'People currently included in the scheduled audience'
                          : 'People who would receive the message using the current settings'}
                      </em>
                    </div>
                    <div className="gmAffectedRole">
                      <span>Students</span>
                      <strong>{areStudentsSelected ? 'Selected' : 'Not selected'}</strong>
                      <em>
                        {areStudentsSelected
                          ? getSelectionDescription(studentsSelection, studentsCount)
                          : 'No students will receive this message directly'}
                      </em>
                    </div>
                    <div className="gmAffectedRole">
                      <span>Parents</span>
                      <strong>{subscriberTypes.parents ? 'Selected' : 'Not selected'}</strong>
                      <em>
                        {subscriberTypes.parents
                          ? parentPriorities.length
                            ? `${
                                Array.isArray(affectedUsers) && affectedUsers.length > 0
                                  ? `${resolvedParentIds.length} ${
                                      resolvedParentIds.length === 1 ? 'parent account' : 'parent accounts'
                                    } selected`
                                  : 'Parents linked to the selected students'
                              } · ${parentPriorities.join(', ')}`
                            : 'Parents linked to the selected students will receive the message'
                          : 'No parents will receive this message'}
                      </em>
                    </div>
                    <div className="gmAffectedRole">
                      <span>Staff</span>
                      <strong>{areStaffSelected ? 'Selected' : 'Not selected'}</strong>
                      <em>
                        {areStaffSelected
                          ? getSelectionDescription(staffSelection, staffCount)
                          : 'No staff members will receive this message'}
                      </em>
                    </div>
                    <div className="gmAffectedRole">
                      <span>Trustees &amp; governors</span>
                      <strong>{areGovernorsSelected ? 'Selected' : 'Not selected'}</strong>
                      <em>
                        {areGovernorsSelected
                          ? getSelectionDescription(governorsSelection, 0)
                          : 'No trustees or governors will receive this message'}
                      </em>
                    </div>
                    <div className="gmAffectedDelivery">
                      <span>
                        {generalMessage.status === GENERAL_MESSAGE_STATUS.SENT
                          ? 'Individual delivery records'
                          : 'Planned notification recipients'}
                      </span>
                      <strong>
                        {generalMessage.status === GENERAL_MESSAGE_STATUS.SCHEDULED
                          ? 'Scheduled audience snapshot'
                          : generalMessage.status === GENERAL_MESSAGE_STATUS.DRAFT
                          ? 'Current audience preview'
                          : 'Per-user delivery details'}
                      </strong>
                      <em>
                        {generalMessage.status === GENERAL_MESSAGE_STATUS.SENT
                          ? 'Open the full recipient list to review every enabled channel and its delivery status.'
                          : generalMessage.status === GENERAL_MESSAGE_STATUS.SCHEDULED
                          ? 'Review the recipients fixed when this message was scheduled and the channels currently available to them.'
                          : 'Resolve the saved audience settings and preview the users and channels that would be used if the message were sent now.'}
                      </em>
                      <button
                        type="button"
                        className="gmButton gmButton--primary"
                        onClick={() => this.showNotificationsForMessage(generalMessage)}
                      >
                        {this.icon('eye')}{' '}
                        {generalMessage.status === GENERAL_MESSAGE_STATUS.SENT
                          ? 'View delivery details'
                          : generalMessage.status === GENERAL_MESSAGE_STATUS.SCHEDULED
                          ? 'View scheduled recipients'
                          : 'Preview recipients'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <footer className="gmDetailsActions" data-help-anchor="gm-detail-actions">
            <button
              type="button"
              className="gmButton gmButton--secondary"
              onClick={() => this.openCopyModal(generalMessage)}
            >
              {this.icon('copy')} Create a new message like this
            </button>
            <div className="gmToolbarSpacer" />
            {generalMessage.status === GENERAL_MESSAGE_STATUS.DRAFT && (
              <button
                type="button"
                className="gmButton gmButton--primary"
                onClick={() => this.goToEditMessage(generalMessage)}
              >
                {this.icon('edit')} Edit
              </button>
            )}
            {generalMessage.status === GENERAL_MESSAGE_STATUS.SCHEDULED && (
              <>
                <button
                  type="button"
                  className="gmButton gmButton--ghost"
                  onClick={() => this.openScheduledActionModal('draft', generalMessage)}
                >
                  Return to draft
                </button>
                <button
                  type="button"
                  className="gmButton gmButton--secondary"
                  onClick={() => this.openScheduledActionModal('reschedule', generalMessage)}
                >
                  {this.icon('clock')} Reschedule
                </button>
                <button
                  type="button"
                  className="gmButton gmButton--primary"
                  onClick={() => this.sendMessageNow(generalMessage)}
                >
                  {this.icon('send')} Send now
                </button>
              </>
            )}
            {generalMessage.status === GENERAL_MESSAGE_STATUS.SENT && (
              <button
                type="button"
                className="gmButton gmButton--primary"
                onClick={() => this.showNotificationsForMessage(generalMessage)}
              >
                {this.icon('eye')} View notifications
              </button>
            )}
          </footer>
        </section>
      </div>
    );
  }

  private renderCopyModal(): React.ReactNode {
    const { isCopyModalOpen, isCopyPreparing, copySourceMessage, copyOptions } = this.state;
    if (!isCopyModalOpen || !copySourceMessage) return null;

    const hasSelection = copyOptions.title || copyOptions.content || copyOptions.recipients;
    const options: Array<{
      key: keyof GeneralMessageCopyOptions;
      title: string;
      description: string;
    }> = [
      {
        key: 'title',
        title: 'Title',
        description: 'Copy the internal title and description.'
      },
      {
        key: 'content',
        title: 'Content',
        description: 'Copy enabled channels, message text and attachments.'
      },
      {
        key: 'recipients',
        title: 'Recipients',
        description: 'Copy the saved audience and skip recipient selection.'
      }
    ];

    return (
      <div
        className="gmModalBackdrop"
        onMouseDown={event => {
          if (event.target === event.currentTarget) this.closeCopyModal();
        }}
      >
        <div
          className="gmModal gmCopyModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-copy-title"
          data-help-anchor="gm-copy-modal"
        >
          <div className="gmCopyModal__heading">
            <span className="gmCopyModal__icon">{this.icon('copy')}</span>
            <div>
              <h2 id="gm-copy-title">Create a new message like this</h2>
              <p title={copySourceMessage.title}>
                Choose what to copy from “{copySourceMessage.title || 'Untitled message'}”.
              </p>
            </div>
          </div>

          <div className="gmCopyOptions" data-help-anchor="gm-copy-options">
            {options.map(option => (
              <label className={`gmCopyOption ${copyOptions[option.key] ? 'is-selected' : ''}`} key={option.key}>
                <input
                  type="checkbox"
                  checked={copyOptions[option.key]}
                  disabled={isCopyPreparing}
                  onChange={() => this.onCopyOptionChange(option.key)}
                />
                <span className="gmCopyOption__check" aria-hidden="true">
                  ✓
                </span>
                <span>
                  <strong>{option.title}</strong>
                  <small>{option.description}</small>
                </span>
              </label>
            ))}
          </div>

          {!hasSelection ? <div className="gmCopyModal__error">Select at least one item to copy.</div> : null}

          <div className="gmModalActions" data-help-anchor="gm-copy-actions">
            <button
              type="button"
              className="gmButton gmButton--ghost"
              disabled={isCopyPreparing}
              onClick={this.closeCopyModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gmButton gmButton--primary"
              disabled={!hasSelection || isCopyPreparing}
              onClick={this.createLikeMessage}
            >
              {isCopyPreparing ? 'Preparing…' : 'Continue to message'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  private renderDeleteModal(): React.ReactNode {
    const { isDeleteModalOpen, selectedItems } = this.state;
    if (!isDeleteModalOpen || !this.isDeleteMessageActive()) return null;
    const scheduledCount = selectedItems.filter(message => message.status === GENERAL_MESSAGE_STATUS.SCHEDULED).length;

    return (
      <div className="gmModalBackdrop">
        <div className="gmModal gmModal--danger" data-help-anchor="gm-delete-modal">
          <div className="gmModalIcon">{this.icon('alert')}</div>
          <h2>Delete selected {selectedItems.length === 1 ? 'message' : 'messages'}?</h2>
          <p>
            This permanently removes {selectedItems.length} selected{' '}
            {selectedItems.length === 1 ? 'message' : 'messages'}.
            {scheduledCount > 0 &&
              ` ${
                scheduledCount === 1 ? 'Its' : `${scheduledCount} scheduled messages’`
              } planned sending will be cancelled.`}{' '}
            This action cannot be undone.
          </p>
          <div className="gmModalActions">
            <button
              type="button"
              className="gmButton gmButton--ghost"
              onClick={() => this.setState({ isDeleteModalOpen: false })}
            >
              Cancel
            </button>
            <button type="button" className="gmButton gmButton--danger" onClick={this.onDeleteMessageClick}>
              {this.icon('trash')} Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  private renderScheduledActionModal(): React.ReactNode {
    const {
      scheduledAction,
      scheduledActionMessage,
      scheduledActionAt,
      scheduledActionError,
      isScheduledActionInProgress
    } = this.state;
    if (!scheduledAction || !scheduledActionMessage) return null;

    const title =
      scheduledAction === 'send'
        ? 'Send this message now?'
        : scheduledAction === 'draft'
        ? 'Return this message to draft?'
        : 'Reschedule message';
    const confirmText =
      scheduledAction === 'send' ? 'Send now' : scheduledAction === 'draft' ? 'Return to draft' : 'Save new time';

    return (
      <div
        className="gmModalBackdrop"
        onMouseDown={event => {
          if (event.target === event.currentTarget) this.closeScheduledActionModal();
        }}
      >
        <div
          className="gmModal gmScheduleActionModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gm-schedule-action-title"
          data-help-anchor="gm-scheduled-action-modal"
        >
          <div className="gmScheduleActionModal__heading">
            <span className="gmScheduleActionModal__icon">
              {this.icon(scheduledAction === 'send' ? 'send' : scheduledAction === 'draft' ? 'edit' : 'clock')}
            </span>
            <div>
              <h2 id="gm-schedule-action-title">{title}</h2>
              <p title={scheduledActionMessage.title}>{scheduledActionMessage.title || 'Untitled message'}</p>
            </div>
          </div>

          {scheduledAction === 'send' && (
            <p className="gmScheduleActionModal__description">
              The planned sending time will be removed and the message will be queued for delivery immediately.
            </p>
          )}
          {scheduledAction === 'draft' && (
            <p className="gmScheduleActionModal__description">
              The planned sending time will be removed. The message will not be sent, and you can edit or schedule it
              again.
            </p>
          )}
          {scheduledAction === 'reschedule' && (
            <div className="gmScheduleActionModal__field">
              <SitDatePicker
                label="New sending date and time"
                mode="datetime"
                value={scheduledActionAt}
                onChange={(date: Date | null) => this.setState({ scheduledActionAt: date, scheduledActionError: '' })}
                min={new Date()}
                minuteStep={5}
                placeholder="Select date and time"
                required
                clearable
                className="gmScheduleDateTimePicker"
              />
            </div>
          )}

          {scheduledActionError && <div className="gmScheduleActionModal__error">{scheduledActionError}</div>}

          <div className="gmModalActions">
            <button
              type="button"
              className="gmButton gmButton--ghost"
              disabled={isScheduledActionInProgress}
              onClick={this.closeScheduledActionModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gmButton gmButton--primary"
              disabled={
                isScheduledActionInProgress ||
                (scheduledAction === 'reschedule' && (!scheduledActionAt || scheduledActionAt.getTime() <= Date.now()))
              }
              onClick={this.submitScheduledAction}
            >
              {isScheduledActionInProgress ? 'Saving…' : confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }

  onEmailSubjectChange = (event): void => {
    const subject = event.target.value;

    this.setState({ messageEmailSubject: subject });
  };

  onEmailBodyChange = (body: any): void => {
    this.setState({ messageEmailBody: body });
  };

  onSmsTextChange = (event): void => {
    const smsText = event.target.value;

    this.setState({ messageSmsText: smsText });
  };

  onPushTitleChange = (event): void => {
    const title = event.target.value;

    this.setState({ messagePushTitle: title });
  };

  onPushBodyChange = (event): void => {
    const body = event.target.value;

    this.setState({ messagePushBody: body });
  };

  onEmailAttachmentChange = async (event): Promise<void> => {
    const { user } = this.props;
    const { maxSizeUploadFile, messageAttachments } = this.state;
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) return;

    input.value = '';
    const currentFileSize = file.size;
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const validation = validateGeneralMessageAttachmentAddition(messageAttachments, currentFileSize, maxSizeUploadFile);

    if (!validation.isValid) {
      this.setState({
        errorMessage: validation.errorMessage || 'This file cannot be attached.',
        isError: true
      });
      return;
    }

    this.setState({ isLoadingFile: true });

    let contentHash: string;
    try {
      contentHash = await getGeneralMessageAttachmentContentHash(file);
    } catch (error) {
      this.setState({
        errorMessage: 'This attachment could not be read. Choose the file again and retry.',
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    const candidateAttachment: Attachment = {
      fileName: fileNameWithoutSpaces,
      externalFileId: '',
      fileSize: currentFileSize,
      contentHash
    };
    if (
      isGeneralMessageAttachmentDuplicate(this.state.messageAttachments, candidateAttachment) ||
      this.pendingAttachmentContentHashes.has(contentHash)
    ) {
      this.setState({
        errorMessage: getGeneralMessageDuplicateAttachmentError(fileName),
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    this.pendingAttachmentContentHashes.add(contentHash);

    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    uploadFileAllPath(user, formData)
      .then(data => {
        const { key, link } = data;
        this.setState(state => ({
          messageAttachments: [
            ...state.messageAttachments,
            {
              fileName: fileNameWithoutSpaces,
              link: link,
              externalFileId: key,
              fileSize: currentFileSize,
              contentHash
            }
          ],
          isLoadingFile: false
        }));
        this.pendingAttachmentContentHashes.delete(contentHash);
      })
      .catch(error => {
        this.pendingAttachmentContentHashes.delete(contentHash);
        this.setState({
          errorMessage: getGeneralMessageAttachmentUploadError(error, maxSizeUploadFile),
          isError: true,
          isLoadingFile: false
        });
      });
  };

  onCloseImageError = () => {
    this.setState({
      isImageError: false
    });
  };

  onIsEmailEditorChange = event => {
    const isEmailEditorEnabled = event.target.checked;

    if (!isEmailEditorEnabled && this.state.isEmailEditorEnabled) {
      this.disabledChannelDrafts.email = {
        subject: this.state.messageEmailSubject,
        body: this.state.messageEmailBody,
        attachments: [...this.state.messageAttachments]
      };
      this.setState({
        isEmailEditorEnabled: false,
        messageEmailSubject: '',
        messageEmailBody: '',
        messageAttachments: []
      });
      return;
    }

    if (isEmailEditorEnabled && !this.state.isEmailEditorEnabled) {
      const cached = this.disabledChannelDrafts.email;
      this.disabledChannelDrafts.email = undefined;
      if (cached) {
        this.setState({
          isEmailEditorEnabled: true,
          messageEmailSubject: cached.subject,
          messageEmailBody: cached.body,
          messageAttachments: [...cached.attachments]
        });
      } else {
        this.setState({ isEmailEditorEnabled: true });
      }
    }
  };

  onIsSmsEditorChange = event => {
    const isSmsEditorEnabled = event.target.checked;

    if (!isSmsEditorEnabled && this.state.isSmsEditorEnabled) {
      this.disabledChannelDrafts.sms = { text: this.state.messageSmsText };
      this.setState({ isSmsEditorEnabled: false, messageSmsText: '' });
      return;
    }

    if (isSmsEditorEnabled && !this.state.isSmsEditorEnabled) {
      const cached = this.disabledChannelDrafts.sms;
      this.disabledChannelDrafts.sms = undefined;
      if (cached) {
        this.setState({ isSmsEditorEnabled: true, messageSmsText: cached.text });
      } else {
        this.setState({ isSmsEditorEnabled: true });
      }
    }
  };

  onIsPushEditorChange = event => {
    const isPushEditorEnabled = event.target.checked;
    if (!isPushEditorEnabled && this.state.isPushEditorEnabled) {
      this.disabledChannelDrafts.push = {
        title: this.state.messagePushTitle,
        body: this.state.messagePushBody
      };
      this.setState({ isPushEditorEnabled: false, messagePushBody: '', messagePushTitle: '' });
      return;
    }
    if (isPushEditorEnabled && !this.state.isPushEditorEnabled) {
      const cached = this.disabledChannelDrafts.push;
      this.disabledChannelDrafts.push = undefined;
      if (cached) {
        this.setState({ isPushEditorEnabled: true, messagePushTitle: cached.title, messagePushBody: cached.body });
      } else {
        this.setState({ isPushEditorEnabled: true });
      }
    }
  };

  onRemoveAttachment = (key: string): void => {
    const attachments = [...this.state.messageAttachments];
    const attachmentIndex = attachments.findIndex(attachment => attachment.externalFileId === key);

    if (attachmentIndex < 0) return;

    attachments.splice(attachmentIndex, 1);

    this.setState({
      messageAttachments: attachments
    });
  };

  getGeneralMessageSummaryTabs() {
    const isOneMessageSelected = this.isOneMessageSelected();

    if (!isOneMessageSelected) {
      return null;
    }

    const {
      currentTabIndex,
      isEditable,
      messageSmsText,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      isMessageRefreshing,
      generalMessage,
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isLoadingFile,
      isImageError,
      isError
    } = this.state;

    if (isMessageRefreshing) {
      return (
        <div>
          <Loader />
        </div>
      );
    }

    const subscribers = propz.get(generalMessage, ['affectedUserList'], []);
    const statistics = propz.get(generalMessage, ['statistics'], {});

    const messageEmailBodyWithoutLink = messageEmailBody.replace(/<div>Attachments:(.+)\s?/, '');

    switch (currentTabIndex) {
      case 0:
        return <GeneralMessageContent message={generalMessage} />;
      case 1:
        if (isEditable) {
          return (
            <div>
              {this.renderSendingError()}
              <GeneralMessageTextForm
                user={this.props.user}
                onEmailSubjectChange={this.onEmailSubjectChange}
                onEmailBodyChange={this.onEmailBodyChange}
                onSmsTextChange={this.onSmsTextChange}
                onPushTitleChange={this.onPushTitleChange}
                onPushBodyChange={this.onPushBodyChange}
                onEmailAttachmentChange={this.onEmailAttachmentChange}
                onRemoveAttachment={this.onRemoveAttachment}
                onCloseImageError={this.onCloseImageError}
                isEditable={isEditable}
                isError={isError}
                isImageError={isImageError}
                isLoadingFile={isLoadingFile}
                messageSmsText={messageSmsText}
                messageEmailSubject={messageEmailSubject}
                messageEmailBody={messageEmailBodyWithoutLink}
                messagePushTitle={messagePushTitle}
                messagePushBody={messagePushBody}
                messageAttachments={messageAttachments}
                isEmailEditorEnabled={isEmailEditorEnabled}
                isSmsEditorEnabled={isSmsEditorEnabled}
                isPushEditorEnabled={isPushEditorEnabled}
                onIsEmailEditorChange={this.onIsEmailEditorChange}
                onIsSmsEditorChange={this.onIsSmsEditorChange}
                onIsPushEditorChange={this.onIsPushEditorChange}
              />
            </div>
          );
        } else {
          return (
            <GeneralMessagePreview
              messageSmsText={messageSmsText}
              messageEmailSubject={messageEmailSubject}
              messageEmailBody={messageEmailBodyWithoutLink}
              messagePushTitle={messagePushTitle}
              messagePushBody={messagePushBody}
              messageAttachments={messageAttachments}
            />
          );
        }
      case 2:
        return <GeneralMessageSubscribers subscribers={subscribers} />;
      case 3:
        return <MessageDeliveryStatus statistics={statistics} />;
    }
  }

  onTabClick = (event, index: number): void => {
    event.preventDefault();

    if (this.state.isEditable) {
      return;
    }

    this.setState({
      currentTabIndex: index
    });
  };

  onGeneralMessageEditButtonClick = (): void => {
    this.setState({
      isEditable: true
    });
  };

  onGeneralMessageSendButtonClick = () => {
    const { selectedItems } = this.state;
    const message = selectedItems[0];
    const generalMessageId = message.id;
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    sendSchoolGeneralMessage(user, generalMessageId).then(message => {
      this.setState({
        selectedItems: [message]
      });
      this.setItems();
    });
  };

  onGeneralMessageSaveButtonClick = () => {
    const {
      messageSmsText,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      selectedItems,
      messageAttachments,
      isEmailEditorEnabled,
      isPushEditorEnabled,
      isSmsEditorEnabled
    } = this.state;

    const attachmentValidation = validateGeneralMessageAttachmentCollection(
      messageAttachments,
      this.state.maxSizeUploadFile
    );

    if (!attachmentValidation.isValid) {
      this.setState({
        isError: true,
        errorMessage: attachmentValidation.errorMessage || 'The attachments could not be validated.'
      });
      return;
    }

    let newMessageEmailBody;
    if (messageEmailBody === '<p><br></p>') {
      newMessageEmailBody = '';
      this.setState({
        messageEmailBody: newMessageEmailBody
      });
    } else {
      newMessageEmailBody = messageEmailBody;
    }

    const isSMSTextExist = messageSmsText !== '';
    const isPushBodyExist = messagePushBody !== '';
    const isPushTitleExist = messagePushTitle !== '';
    const isEmailSubjectExist = messageEmailSubject !== '';
    const isEmailBodyExist = newMessageEmailBody !== '';

    const isShowSMSError = !isSMSTextExist && isSmsEditorEnabled;
    const isShowPushError = (!isPushBodyExist || !isPushTitleExist) && isPushEditorEnabled;
    const isShowEmailError = (!isEmailSubjectExist || !isEmailBodyExist) && isEmailEditorEnabled;

    if (isShowSMSError || isShowPushError || isShowEmailError) {
      let errorText = '';

      switch (true) {
        case isShowSMSError:
          errorText = 'Text for sms content should be defined';
          break;
        case isShowPushError:
          errorText = 'Title or body for push content should be defined';
          break;
        case isShowEmailError:
          errorText = 'Subject or body for email content should be defined';
          break;
      }
      this.setState({
        isError: true,
        errorMessage: errorText
      });
    } else {
      const selectedMessage = selectedItems[0];
      const selectedMessageId = selectedMessage.id;

      const messageHTMLEmailBody =
        newMessageEmailBody !== ''
          ? `${HTML_FIRST_PART_PAGE_CODE} ${newMessageEmailBody} ${HTML_LAST_PART_PAGE_CODE}`
          : '';

      const data = {
        deliveryChannels: {
          sms: isSmsEditorEnabled,
          email: isEmailEditorEnabled,
          push: isPushEditorEnabled
        },
        content: {
          sms: { text: messageSmsText },
          email: { subject: messageEmailSubject, body: messageHTMLEmailBody, attachments: messageAttachments },
          push: { title: messagePushTitle, body: messagePushBody }
        }
      };

      this.setState({
        isEditable: false,
        isLoading: true
      });

      const { user } = this.props;

      updateSchoolGeneralMessage(user, data, selectedMessageId)
        .then(messageUpdated => {
          this.setState({
            selectedItems: [messageUpdated]
          });

          this.setItems();
        })
        .catch(error => {
          this.setState({
            isEditable: true,
            isLoading: false,
            isError: true,
            errorMessage: getGeneralMessageRequestError(error, 'Error saving general message')
          });
        });
    }
  };

  onGeneralMessageShowNotificationsButtonClick = () => {
    const { selectedItems } = this.state;
    const message = selectedItems[0];
    this.showNotificationsForMessage(message);
  };

  onRefreshClick = (): void => {
    this.setState({ isMessageRefreshing: true });

    const { selectedItems } = this.state;
    const message = selectedItems[0];
    const generalMessageId = message.id;
    const user = this.props.user;

    getSchoolGeneralMessage(user, generalMessageId).then(message => {
      this.setState({
        generalMessage: message,
        isMessageRefreshing: false
      });
    });
  };

  onCloseErrorClick = event => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  private goToEditDraft = () => {
    const { history, user } = this.props;
    const { selectedItems, generalMessage } = this.state;
    if (!selectedItems || selectedItems.length !== 1) return;

    const messageId = selectedItems[0].id;
    history.push({
      pathname: `/generalMessages/${messageId}/edit`,
      state: {
        search: history.location.search,
        user,
        generalMessage
      }
    });
  };

  renderSummary(): React.ReactNode {
    const {
      selectedItems,
      currentTabIndex,
      isEditable,
      isLoadingFile,
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isError
    } = this.state;

    const isOneMessageSelected = this.isOneMessageSelected();
    const generalMessageSummaryTabs = this.getGeneralMessageSummaryTabs();
    const isMessageStatusDraft = isOneMessageSelected && selectedItems[0].status === GENERAL_MESSAGE_STATUS.DRAFT;
    const isMessageStatusSent = isOneMessageSelected && selectedItems[0].status === GENERAL_MESSAGE_STATUS.SENT;
    const isMessageStatusScheduled =
      isOneMessageSelected && selectedItems[0].status === GENERAL_MESSAGE_STATUS.SCHEDULED;
    const isShowScheduleInfo = (currentTabIndex === 0 || currentTabIndex === 3) && isMessageStatusScheduled;
    const isShowEditButton = isMessageStatusDraft && currentTabIndex === 1 && !isEditable;
    const isShowSaveButton = isMessageStatusDraft && currentTabIndex === 1 && isEditable;
    const isShowRefreshButton = isMessageStatusSent && currentTabIndex === 3;
    const isShowSendButton = (currentTabIndex === 0 || currentTabIndex === 3) && isMessageStatusDraft;
    const isAllEditorDisabled = !isSmsEditorEnabled && !isPushEditorEnabled && !isEmailEditorEnabled;

    if (!isOneMessageSelected) {
      return <div className="font-weight-bold">Select a general message above</div>;
    }

    return (
      <div>
        {isOneMessageSelected && (
          <GenericSummary currentTabIndex={currentTabIndex} onTabClick={this.onTabClick} tabs={GENERAL_MESSAGE_TABS}>
            {isMessageStatusDraft && currentTabIndex === 0 && (
              <Button onClick={this.goToEditDraft} text={'Edit message'} customClass={'mt-3 mb-3 mr-3'} />
            )}
            {isShowEditButton && (
              <Button onClick={this.onGeneralMessageEditButtonClick} text={'Edit message'} customClass={'mt-3'} />
            )}
            {isShowScheduleInfo && (
              <ScheduleInfo
                user={this.props.user}
                messageId={selectedItems[0].id}
                onSendNowClick={this.onGeneralMessageSendButtonClick}
              />
            )}
            {isShowSaveButton && (
              <Button
                onClick={this.onGeneralMessageSaveButtonClick}
                disabled={isLoadingFile || isAllEditorDisabled || isError}
                text={'Save message'}
                customClass={'mt-3 mb-3'}
              />
            )}
            {isShowSendButton && !isShowScheduleInfo && (
              <Button onClick={this.onGeneralMessageSendButtonClick} text={'Send message'} customClass={'mt-3 mb-3'} />
            )}
            {isOneMessageSelected && isMessageStatusSent && currentTabIndex === 0 && (
              <Button
                onClick={this.onGeneralMessageShowNotificationsButtonClick}
                text={'Show notifications'}
                customClass={'mt-3 mb-3'}
              />
            )}
            {isShowRefreshButton && (
              <>
                <Button
                  onClick={this.onRefreshClick}
                  text={[
                    <FontAwesomeIcon key={'general_message_refresh_button'} className={'mr-2'} icon="sync-alt" />,
                    'Refresh'
                  ]}
                  customClass={'mt-3 mb-3 mr-3'}
                />
                <Button
                  onClick={this.onGeneralMessageShowNotificationsButtonClick}
                  text={'Show notifications'}
                  customClass={'mt-3 mb-3'}
                />
              </>
            )}
            {generalMessageSummaryTabs}
          </GenericSummary>
        )}
      </div>
    );
  }

  renderSendingError(): React.ReactNode {
    const { errorMessage, isError } = this.state;

    if (!isError) {
      return null;
    }

    return (
      <div className="alert fade show alert-danger d-flex justify-content-between align-items-center" role="alert">
        {errorMessage}
        <button
          type="button"
          className="ml-3 btn btn-danger"
          aria-label="Close"
          onClick={event => this.onCloseErrorClick(event)}
        >
          Close
        </button>
      </div>
    );
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return this.renderMessagesTable();
  }
}
